import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useLocation, useNavigate } from '@tanstack/react-router';
import './lib/utils';
import { getStoredGitAliases, resolveGitAuthor } from './components/SettingsView';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  AreaChart, Area, PieChart, Pie, Cell
} from 'recharts';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowRightFromBracket,
  faArrowTrendDown,
  faArrowTrendUp,
  faBell,
  faCalendarDays,
  faChartBar,
  faChevronDown,
  faChevronLeft,
  faChevronRight,
  faCircleCheck,
  faClock,
  faClockRotateLeft,
  faCloud,
  faCode,
  faCodeBranch,
  faCodeCommit,
  faCodeMerge,
  faDesktop,
  faDownload,
  faFile,
  faFileLines,
  faFolderOpen,
  faGauge,
  faGear,
  faGlobe,
  faChartLine,
  faLayerGroup,
  faLock,
  faMagnifyingGlass,
  faMoon,
  faRotate,
  faScroll,
  faSpinner,
  faStar,
  faSun,
  faTerminal,
  faTimeline,
  faTriangleExclamation,
  faTrophy,
  faTv,
  faUsers,
  faWandMagicSparkles,
  faWifi,
  faXmark
} from '@fortawesome/free-solid-svg-icons';
import { faGithub, faBitbucket, faJira, faTrello, faConfluence, faGitlab, faNotion, faGoogle, faSlack, faMicrosoft } from '@fortawesome/free-brands-svg-icons';
import { motion, AnimatePresence } from 'framer-motion';
import { LiveBoard } from '@/components/LiveBoard';
import { CrossProjectView } from '@/components/CrossProjectView';
import { SettingsView } from '@/components/SettingsView';
import { JiraActivityView } from '@/components/JiraActivityView';
import { CombinedProjectView } from '@/components/CombinedProjectView';
import { AppSidebar, type SidebarAction, SIDEBAR_WIDTH, SIDEBAR_COLLAPSED_WIDTH } from '@/components/AppSidebar';
import { AnimatedBlobs } from '@/components/AnimatedBlobs';
import { IndicatorConfigEditor } from '@/components/IndicatorConfigEditor';
import { LinearActivityView } from '@/components/LinearActivityView';
import { MondayActivityView } from '@/components/MondayActivityView';
import { ConfluenceActivityView } from '@/components/ConfluenceActivityView';
import { NotionActivityView } from '@/components/NotionActivityView';
import { GoogleDocsActivityView } from '@/components/GoogleDocsActivityView';
import { TrelloActivityView } from '@/components/TrelloActivityView';
import { SlackView } from '@/components/SlackView';
import { TeamsView } from '@/components/TeamsView';
import { getSlackSettings, getTeamsSettings } from '@/services/messaging-storage';
import { atlassianOAuth2 } from '@/services/atlassian-oauth2';
import { linearOAuth2 } from '@/services/linear-oauth2';
import { mondayOAuth2 } from '@/services/monday-oauth2';
import { AuditHistoryView } from '@/components/AuditHistoryView';
import { ReportsView } from '@/components/ReportsView';
import { ChangelogView } from '@/components/ChangelogView';
import { DashboardView } from '@/components/DashboardView';
import { TimelineView } from '@/components/TimelineView';
import { RepoChangelogTab } from '@/components/RepoChangelogTab';
import { notifyAnalysisComplete } from '@/services/notifications';
import { NotificationPermissionDialog } from '@/components/NotificationPermissionDialog';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { DateRangePicker } from '@/components/ui/date-range-picker';
import { timeAgo, formatNumber } from '@/lib/utils';
import { DateRange } from 'react-day-picker';
import { subDays } from 'date-fns';
import * as api from '@/services/api';
import { generateAndDownloadReport } from '@/services/api';
import * as historyStore from '@/services/analysis-history-store';
import * as audit from '@/services/audit';
import type { CombinedReviewData } from '@/components/CombinedProjectView';
import type { CrossProjectReviewData } from '@/components/CrossProjectView';
import type { JiraReviewData } from '@/components/JiraActivityView';
import type { LinearReviewData } from '@/components/LinearActivityView';
import type { TrelloReviewData } from '@/components/TrelloActivityView';
import type { ConfluenceReviewData } from '@/components/ConfluenceActivityView';

export interface GitReviewData {
  stats: RepoStats;
  commits: Commit[];
  repoName: string;
  dateRange?: { from: string; to: string };
}

interface Commit {
  hash: string;
  author: string;
  email: string;
  date: string;
  message: string;
  branch: string;
  additions: number;
  deletions: number;
  isMerge?: boolean;
}

interface AuthorStats {
  name: string;
  email: string;
  commits: number;
  additions: number;
  deletions: number;
  firstCommit: string;
  lastCommit: string;
  activeDays: number;
  avgCommitsPerDay: number;
}

interface DailyActivity {
  date: string;
  commits: number;
  additions: number;
  deletions: number;
  authors: string[];
}

interface BranchRecentCommit {
  hash: string;
  author: string;
  email: string;
  date: string;
  message: string;
}

interface BranchInfo {
  name: string;
  isRemote: boolean;
  lastCommit: string;
  lastAuthor: string;
  commitCount: number;
  recentCommits: BranchRecentCommit[];
}

interface RepoStats {
  totalCommits: number;
  totalAuthors: number;
  totalBranches: number;
  totalAdditions: number;
  totalDeletions: number;
  authors: AuthorStats[];
  dailyActivity: DailyActivity[];
  branches: BranchInfo[];
  dateRange: {
    start: string;
    end: string;
  };
}

const CHART_COLORS = [
  'hsl(221, 83%, 53%)',
  'hsl(160, 60%, 45%)',
  'hsl(30, 80%, 55%)',
  'hsl(280, 65%, 60%)',
  'hsl(340, 75%, 55%)',
  'hsl(200, 70%, 50%)',
  'hsl(120, 50%, 45%)',
  'hsl(45, 90%, 50%)',
];

type RepoType = 'local' | 'bitbucket' | 'github' | 'gitlab';


interface RepoConfig {
  type: RepoType;
  path?: string;
  url?: string;
  accessToken?: string;
  username?: string;
  appPassword?: string;
  weeks?: number;
  hash?: string;
  name?: string;
}

interface PredefinedRepo {
  id: string;
  name: string;
  description?: string;
  type: RepoType;
  url?: string;
  path?: string;
  accessToken?: string;
  username?: string;
  appPassword?: string;
  icon?: string;
  color?: string;
}

interface AnalysisHistory {
  id: string;
  timestamp?: string;
  createdAt?: string;
  repoName: string;
  repoType: string;
  repoUrl?: string;
  stats?: {
    totalCommits: number;
    totalAuthors: number;
    totalBranches?: number;
    totalAdditions?: number;
    totalDeletions?: number;
  };
  totalCommits?: number;
  totalAuthors?: number;
  dateRange?: { start: string; end: string };
  topAuthors?: { name: string; email?: string; commits: number; additions: number; deletions: number; activeDays: number }[];
  topBranches?: { name: string; isRemote: boolean; commitCount: number; lastAuthor?: string }[];
  duration?: number;
  durationMs?: number;
  success: boolean;
}

interface LiveCommit {
  hash: string;
  shortHash: string;
  author: string;
  message: string;
  date: string;
  isNew: boolean;
}

interface AuditEntry {
  timestamp?: string;
  createdAt?: string;
  action: string;
  details?: Record<string, unknown>;
  repoName?: string;
  success: boolean;
  error?: string;
  duration?: number;
  durationMs?: number;
}

interface PerformanceTarget {
  id: string;
  authorName: string;
  authorEmail?: string;
  createdAt: string;
  updatedAt: string;
  period: { start: string; end: string };
  targets: {
    minCommits?: number;
    minActiveDays?: number;
    minAdditions?: number;
    maxDeletions?: number;
    minNetLines?: number;
  };
  notes?: string;
  reason?: string; // Reason for creating/modifying target
  cancelReason?: string; // Reason for cancelling/removing target
  status: 'active' | 'completed' | 'cancelled';
}

interface BitbucketRepo {
  slug: string;
  name: string;
  fullName: string;
  description: string;
  isPrivate: boolean;
  project: string | null;
  projectKey: string | null;
  workspace: string;
  url: string;
  cloneUrl: string;
  updatedOn: string;
  language: string | null;
  size: number;
}

interface GitHubRepo {
  id: number;
  name: string;
  fullName: string;
  description: string;
  isPrivate: boolean;
  owner: string;
  url: string;
  cloneUrl: string;
  updatedAt: string;
  language: string | null;
  size: number;
  defaultBranch: string;
  stargazersCount: number;
  forksCount: number;
}

interface GitLabRepo {
  id: number;
  name: string;
  fullName: string;
  description: string;
  isPrivate: boolean;
  owner: string;
  url: string;
  cloneUrl: string;
  sshUrl: string;
  updatedAt: string;
  language: string | null;
  defaultBranch: string;
  starCount: number;
  forksCount: number;
  namespace: string;
  avatarUrl: string | null;
}

interface AppProps {
  username?: string | null;
  onLogout?: () => void;
  onLogin?: (email: string, token: string, bbUsername?: string, ghUsername?: string, ghToken?: string) => Promise<void>;
  storedUsername?: string | null;
  storedEmail?: string | null;
  storedToken?: string | null;
  githubUsername?: string | null;
  githubToken?: string | null;
}

function App({ onLogout, onLogin, storedUsername, storedEmail, storedToken, githubUsername, githubToken }: AppProps = {}) {
  // Predefined repos
  const [predefinedRepos, setPredefinedRepos] = useState<PredefinedRepo[]>([]);
  const [selectedPredefined, setSelectedPredefined] = useState<PredefinedRepo | null>(null);

  // Resolved git provider credentials (from IndexedDB settings, not props)
  const [resolvedBbEmail, setResolvedBbEmail] = useState<string | null>(storedEmail || null);
  const [resolvedBbToken, setResolvedBbToken] = useState<string | null>(storedToken || null);
  const [resolvedBbUsername, setResolvedBbUsername] = useState<string | null>(storedUsername || null);
  const [resolvedGhToken, setResolvedGhToken] = useState<string | null>(githubToken || null);
  const [resolvedGhUsername, setResolvedGhUsername] = useState<string | null>(githubUsername || null);

  // Bitbucket repos from API
  const [bitbucketRepos, setBitbucketRepos] = useState<BitbucketRepo[]>([]);
  const [bitbucketReposLoading, setBitbucketReposLoading] = useState(false);
  const [bitbucketReposError, setBitbucketReposError] = useState<string | null>(null);
  const [bitbucketSearch, setBitbucketSearch] = useState('');

  // GitHub repos from API
  const [githubRepos, setGithubRepos] = useState<GitHubRepo[]>([]);
  const [githubReposLoading, setGithubReposLoading] = useState(false);
  const [githubReposError, setGithubReposError] = useState<string | null>(null);
  const [githubSearch, setGithubSearch] = useState('');

  // GitLab repos from API
  const [gitlabRepos, setGitlabRepos] = useState<GitLabRepo[]>([]);
  const [gitlabReposLoading, setGitlabReposLoading] = useState(false);
  const [gitlabReposError, setGitlabReposError] = useState<string | null>(null);
  const [gitlabSearch, setGitlabSearch] = useState('');
  const [resolvedGlToken, setResolvedGlToken] = useState<string | null>(null);
  const [resolvedGlInstanceUrl, setResolvedGlInstanceUrl] = useState<string>('https://gitlab.com');

  const [bitbucketCollapsed, setBitbucketCollapsed] = useState(false);
  const [githubCollapsed, setGithubCollapsed] = useState(false);
  const [gitlabCollapsed, setGitlabCollapsed] = useState(false);
  const [quickAccessCollapsed, setQuickAccessCollapsed] = useState(false);
  const [defaultRepoName, setDefaultRepoName] = useState<string | null>(() => {
    return localStorage.getItem('verigit-default-repo');
  });
  const [hasAutoLoaded, setHasAutoLoaded] = useState(false);
  
  // Repo configuration
  const [repoType, setRepoType] = useState<RepoType>('local');
  const [localPath, setLocalPath] = useState<string>('');
  const [remoteUrl, setRemoteUrl] = useState<string>('');
  
  const [dateRange, setDateRange] = useState<DateRange | undefined>({
    from: subDays(new Date(), 28),
    to: new Date()
  });
  const [stats, setStats] = useState<RepoStats | null>(null);
  const [commits, setCommits] = useState<Commit[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedCommit, setSelectedCommit] = useState<Commit | null>(null);
  const [diff, setDiff] = useState<string>('');
  const [diffLoading, setDiffLoading] = useState(false);
  const [expandedDiffFiles, setExpandedDiffFiles] = useState<Set<number>>(new Set());
  const [authorFilter, setAuthorFilter] = useState<string>('all');
  const [activeTab, setActiveTab] = useState<string>('overview');
  const [lastRefresh, setLastRefresh] = useState<Date>(new Date());
  const [currentTime, setCurrentTime] = useState<Date>(new Date());
  
  // Branch tab state
  const [branchFilter, setBranchFilter] = useState<'all' | 'local' | 'remote'>('all');
  const [branchSort, setBranchSort] = useState<'recent' | 'name' | 'commits'>('recent');
  const [selectedBranch, setSelectedBranch] = useState<string | null>(null);
  const [expandedBranchCommits, setExpandedBranchCommits] = useState<Set<string>>(new Set());
  

  // Clear analysis state when switching repos
  const clearAnalysisState = useCallback(() => {
    setStats(null);
    setCommits([]);
    setError(null);
    setLogs([]);
    setShowConsole(false);
    setSelectedCommit(null);
    setDiff('');
    setAuthorFilter('all');
    setActiveTab('overview');
    setSelectedBranch(null);
    setExpandedBranchCommits(new Set());
  }, []);

  // Console output
  const [logs, setLogs] = useState<string[]>([]);
  const [showConsole, setShowConsole] = useState(false);
  const [sessionId] = useState(() => `session-${Date.now()}-${Math.random().toString(36).slice(2)}`);
  const consoleRef = useRef<HTMLDivElement>(null);
  const [autoScroll, setAutoScroll] = useState(true);
  const scrollTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  
  // Connection & live features
  const [isConnected, setIsConnected] = useState(true);
  const [history, setHistory] = useState<AnalysisHistory[]>([]);
  const [liveCommits, setLiveCommits] = useState<LiveCommit[]>([]);
  const [showNotifications, setShowNotifications] = useState(false);
  const [auditLog, setAuditLog] = useState<AuditEntry[]>([]);
  const [showAuditHistoryView, setShowAuditHistoryView] = useState(false);
  const [showReportsView, setShowReportsView] = useState(false);
  const [showChangelogView, setShowChangelogView] = useState(false);
  const [showRepoChangelogView, setShowRepoChangelogView] = useState(false);
  const [selectedChangelogRepo, setSelectedChangelogRepo] = useState<{ id: string; name: string; type: RepoType; url?: string; path?: string; accessToken?: string; username?: string; appPassword?: string } | null>(null);
  const [showLiveBoard, setShowLiveBoard] = useState(false);
  const [showCrossProjectView, setShowCrossProjectView] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [showJiraMode, setShowJiraMode] = useState(false);
  const [showNotionMode, setShowNotionMode] = useState(false);
  const [showGoogleDocsMode, setShowGoogleDocsMode] = useState(false);
  const [showTimelineView, setShowTimelineView] = useState(false);
  const [showCombinedView, setShowCombinedView] = useState(false);
  const [showDashboardView, setShowDashboardView] = useState(true);
  const [showIndicatorEditor, setShowIndicatorEditor] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [showRainbowBg, setShowRainbowBg] = useState(true);

  // View mounting state (for lazy loading + persistence)
  const [mountedViews, setMountedViews] = useState<Set<string>>(new Set());
  
  // Helper to mark a view as mounted when opened
  const activateView = useCallback((viewId: string, setter: (val: boolean) => void) => {
    setMountedViews(prev => {
      const next = new Set(prev);
      next.add(viewId);
      return next;
    });
    setter(true);
  }, []);

  const [themeMode, setThemeMode] = useState<'light' | 'dark' | 'system'>(() => {
    if (typeof window !== 'undefined') {
      const stored = localStorage.getItem('verigit-theme');
      if (stored === 'light' || stored === 'dark' || stored === 'system') return stored;
      const oldFlag = localStorage.getItem('verigit-dark-mode');
      if (oldFlag === 'true') return 'dark';
      if (oldFlag === 'false') return 'light';
    }
    return 'system';
  });
  const isDarkMode = themeMode === 'dark' || (themeMode === 'system' && (typeof window !== 'undefined' ? window.matchMedia('(prefers-color-scheme: dark)').matches : false));

  useEffect(() => {
    const root = document.documentElement;
    const applyTheme = (dark: boolean) => {
      if (dark) root.classList.add('dark');
      else root.classList.remove('dark');
    };

    localStorage.setItem('verigit-theme', themeMode);
    if (themeMode === 'dark') {
      localStorage.setItem('verigit-dark-mode', 'true');
      applyTheme(true);
    } else if (themeMode === 'light') {
      localStorage.setItem('verigit-dark-mode', 'false');
      applyTheme(false);
    } else {
      localStorage.removeItem('verigit-dark-mode');
      applyTheme(window.matchMedia('(prefers-color-scheme: dark)').matches);
    }

    const handler = (e: MediaQueryListEvent) => {
      if (themeMode !== 'system') return;
      applyTheme(e.matches);
    };
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, [themeMode]);

  const [showLinearMode, setShowLinearMode] = useState(false);
  const [showMondayMode, setShowMondayMode] = useState(false);
  const [showTrelloMode, setShowTrelloMode] = useState(false);
  const [showConfluenceMode, setShowConfluenceMode] = useState(false);
  const [confluenceEnabled, setConfluenceEnabled] = useState(false);

  // ==================== URL ↔ View State Sync ====================
  const location = useLocation();
  const navigate = useNavigate();

  // Helper to close all views
  const closeAllViews = useCallback(() => {
    setShowCrossProjectView(false);
    setShowSettings(false);
    setShowJiraMode(false);
    setShowTimelineView(false);
    setShowLinearMode(false);
    setShowMondayMode(false);
    setShowTrelloMode(false);
    setShowConfluenceMode(false);
    setShowNotionMode(false);
    setShowGoogleDocsMode(false);
    setShowSlackMode(false);
    setShowTeamsMode(false);
    setShowCombinedView(false);
    setShowIndicatorEditor(false);
    setShowAuditHistoryView(false);
    setShowReportsView(false);
    setShowChangelogView(false);
    setShowRepoChangelogView(false);
    setShowDashboardView(false);
    // Clear any review data
    setCombinedReviewData(undefined);
    setCrossProjectReviewData(undefined);
    setJiraReviewData(undefined);
    setLinearReviewData(undefined);
    setTrelloReviewData(undefined);
    setConfluenceReviewData(undefined);
  }, []);

  // Map URL path segments to view state setters
  const PAGE_ROUTES: Record<string, () => void> = useMemo(() => ({
    'single-repo': () => { closeAllViews(); }, // Single repo analysis is the default view (no specific view active)
    'cross-project': () => activateView('cross-project', setShowCrossProjectView),
    'combined': () => activateView('combined', setShowCombinedView),
    'jira': () => activateView('jira', setShowJiraMode),
    'roadmap': () => activateView('roadmap', setShowTimelineView),
    'linear': () => activateView('linear', setShowLinearMode),
    'monday': () => activateView('monday', setShowMondayMode),
    'trello': () => activateView('trello', setShowTrelloMode),
    'confluence': () => activateView('confluence', setShowConfluenceMode),
    'notion': () => activateView('notion', setShowNotionMode),
    'google-docs': () => activateView('google-docs', setShowGoogleDocsMode),
    'slack': () => activateView('slack', setShowSlackMode),
    'teams': () => activateView('teams', setShowTeamsMode),
    'audit': () => activateView('audit', setShowAuditHistoryView),
    'reports': () => activateView('reports', setShowReportsView),
    'changelog': () => activateView('changelog', setShowChangelogView),
    'repo-changelog': () => activateView('repo-changelog', setShowRepoChangelogView),
    'indicators': () => activateView('indicators', setShowIndicatorEditor),
    'settings': () => activateView('settings', setShowSettings),
    'dashboard': () => activateView('dashboard', setShowDashboardView),
  }), [activateView, closeAllViews]);

  // Navigate to a page and update the URL
  const navigateToPage = useCallback((page: string | null) => {
    const target = page ? `/app/${page}` : '/app';
    if (location.pathname !== target) {
      navigate({ to: target });
    }
  }, [location.pathname, navigate]);

  // Sync URL → view state on mount and when URL changes
  useEffect(() => {
    const path = location.pathname;
    const segment = path.startsWith('/app/') ? path.slice(5) : null;

    if (segment && PAGE_ROUTES[segment]) {
      // Close all views first, then activate the correct one
      setShowDashboardView(false);
      setShowCrossProjectView(false);
      setShowSettings(false);
      setShowJiraMode(false);
      setShowTimelineView(false);
      setShowLinearMode(false);
      setShowMondayMode(false);
      setShowTrelloMode(false);
      setShowConfluenceMode(false);
      setShowNotionMode(false);
      setShowGoogleDocsMode(false);
      setShowSlackMode(false);
      setShowTeamsMode(false);
      setShowCombinedView(false);
      setShowIndicatorEditor(false);
      setShowAuditHistoryView(false);
      setShowReportsView(false);
      PAGE_ROUTES[segment]();
    } else if (!segment) {
      // No URL segment - navigate to dashboard
      navigateToPage('dashboard');
    }
  }, [location.pathname, PAGE_ROUTES, navigateToPage]);

  // Review data for reopening past analyses
  const [combinedReviewData, setCombinedReviewData] = useState<CombinedReviewData | undefined>();
  const [crossProjectReviewData, setCrossProjectReviewData] = useState<CrossProjectReviewData | undefined>();
  const [jiraReviewData, setJiraReviewData] = useState<JiraReviewData | undefined>();
  const [linearReviewData, setLinearReviewData] = useState<LinearReviewData | undefined>();
  const [trelloReviewData, setTrelloReviewData] = useState<TrelloReviewData | undefined>();
  const [confluenceReviewData, setConfluenceReviewData] = useState<ConfluenceReviewData | undefined>();
  const [slackReviewSnapshot, setSlackReviewSnapshot] = useState<import('./services/messaging-types').MessagingSnapshot | undefined>();
  const [teamsReviewSnapshot, setTeamsReviewSnapshot] = useState<import('./services/messaging-types').MessagingSnapshot | undefined>();
  const [jiraConfig, setJiraConfig] = useState<{ enabled: boolean; domain: string; email: string; apiToken: string; defaultProjectKey?: string; defaultBoardId?: number; priorityUsers?: string[] } | null>(null);
  const [linearConfig, setLinearConfig] = useState<{ enabled: boolean; apiKey: string } | null>(null);
  const [linearOAuthEnabled, setLinearOAuthEnabled] = useState(false);
  const [mondayConfig, setMondayConfig] = useState<{ enabled: boolean; apiKey: string } | null>(null);
  const [mondayOAuthEnabled, setMondayOAuthEnabled] = useState(false);
  const [trelloConfig, setTrelloConfig] = useState<{ enabled: boolean; apiKey: string; token: string } | null>(null);
  const [notionConfig, setNotionConfig] = useState<{ enabled: boolean; apiKey: string; workspaceName?: string } | null>(null);
  const [googleDocsConfig, setGoogleDocsConfig] = useState<{ enabled: boolean; accessToken?: string } | null>(null);
  const [showSlackMode, setShowSlackMode] = useState(false);
  const [showTeamsMode, setShowTeamsMode] = useState(false);
  const [slackConnected, setSlackConnected] = useState(false);
  const [teamsConnected, setTeamsConnected] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [refreshInterval, setRefreshInterval] = useState(30); // seconds
  const [lastAutoRefresh, setLastAutoRefresh] = useState<Date | null>(null);
  const [isAutoRefreshing, setIsAutoRefreshing] = useState(false);
  
  // Individual contributor view
  const [selectedAuthor, setSelectedAuthor] = useState<AuthorStats | null>(null);
  const [showAuthorDialog, setShowAuthorDialog] = useState(false);
  const [authorTargets, setAuthorTargets] = useState<PerformanceTarget[]>([]);
  const [showTargetForm, setShowTargetForm] = useState(false);
  const [newTarget, setNewTarget] = useState({
    minCommits: '',
    minActiveDays: '',
    minAdditions: '',
    periodStart: '',
    periodEnd: '',
    notes: '',
    reason: '',
  });
  const [deleteTargetId, setDeleteTargetId] = useState<string | null>(null);
  const [deleteReason, setDeleteReason] = useState('');
  const [allTargets, setAllTargets] = useState<PerformanceTarget[]>([]);
  
  // Load git provider credentials from IndexedDB on mount.
  // First run migrateLegacyGitCredentials() to silently rescue any credentials
  // that were stored in the old verigit-auth localStorage key before local auth was introduced.
  useEffect(() => {
    api.migrateLegacyGitCredentials().finally(() => {
      api.getBitbucketSettings().then(s => {
        if (s?.email) {
          setResolvedBbEmail(s.email);
          setResolvedBbToken(s.token);
          setResolvedBbUsername(s.username || null);
        }
      }).catch(() => {});
      api.getGitHubSettings().then(s => {
        if (s?.token) {
          setResolvedGhToken(s.token);
          setResolvedGhUsername(s.username || null);
        }
      }).catch(() => {});
    });
  }, []);

  // Fetch Bitbucket repos from API
  useEffect(() => {
    if (resolvedBbEmail && resolvedBbToken) {
      setBitbucketReposLoading(true);
      setBitbucketReposError(null);
      api.getBitbucketRepos(resolvedBbEmail, resolvedBbToken)
        .then(data => {
          setBitbucketRepos(data.repos || []);
        })
        .catch(err => setBitbucketReposError(err.message))
        .finally(() => setBitbucketReposLoading(false));
    }
  }, [resolvedBbEmail, resolvedBbToken]);

  // Fetch GitHub repos from API
  useEffect(() => {
    if (resolvedGhToken) {
      setGithubReposLoading(true);
      setGithubReposError(null);
      api.getGitHubRepos(resolvedGhToken)
        .then(data => {
          setGithubRepos(data.repos || []);
        })
        .catch(err => setGithubReposError(err.message))
        .finally(() => setGithubReposLoading(false));
    }
  }, [resolvedGhToken]);

  // Fetch GitLab repos from API
  useEffect(() => {
    if (resolvedGlToken) {
      setGitlabReposLoading(true);
      setGitlabReposError(null);
      api.getGitLabRepos(resolvedGlToken, resolvedGlInstanceUrl)
        .then(data => {
          setGitlabRepos(data.repos || []);
        })
        .catch(err => setGitlabReposError(err.message))
        .finally(() => setGitlabReposLoading(false));
    }
  }, [resolvedGlToken, resolvedGlInstanceUrl]);

  // Audit session start
  useEffect(() => {
    audit.auditSessionStart();
    if (storedUsername || storedEmail) {
      audit.auditLogin('credentials', storedEmail || storedUsername || undefined);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch predefined repos and history on mount
  useEffect(() => {
    api.getRepos()
      .then(data => {
        const repos = (data.predefined || []) as unknown as PredefinedRepo[];
        setPredefinedRepos(repos);
        
        // Auto-select default repo if set
        if (defaultRepoName && !hasAutoLoaded) {
          const defaultRepo = repos.find((r: PredefinedRepo) => r.name === defaultRepoName);
          if (defaultRepo) {
            setSelectedPredefined(defaultRepo);
          }
        }
      })
      .catch(err => console.error('Failed to load predefined repos:', err));
    
    api.getHistory()
      .then(data => setHistory(data))
      .catch(err => console.error('Failed to load history:', err));
    
    api.getTargets()
      .then(data => setAllTargets(Array.isArray(data) ? data : []))
      .catch(err => console.error('Failed to load targets:', err));
    
    // Load Jira settings (IndexedDB)
    api.getJiraSettings()
      .then(data => {
        console.log('[Jira] IndexedDB settings:', data);
        if (data && data.domain) {
          setJiraConfig({ ...data, enabled: true });
        } else {
          // IndexedDB empty — try localStorage fallback
          const stored = localStorage.getItem('verigit-jira-settings');
          console.log('[Jira] LocalStorage fallback:', stored ? 'found' : 'empty');
          if (stored) {
            try {
              const parsed = JSON.parse(stored);
              if (parsed.domain) {
                console.log('[Jira] Restoring from localStorage:', parsed.domain);
                setJiraConfig({ ...parsed, enabled: true });
                // Re-sync to IndexedDB
                api.saveJiraSettings({ ...parsed, enabled: true }).catch(() => {});
              }
            } catch {}
          }
        }
      })
      .catch(() => {
        // Error — try localStorage
        try {
          const stored = localStorage.getItem('verigit-jira-settings');
          if (stored) { const parsed = JSON.parse(stored); if (parsed.domain) setJiraConfig({ ...parsed, enabled: true }); }
        } catch {}
      });

    // Load platform settings (Linear, Monday, Trello)
    api.getLinearSettings()
      .then(data => { if (data?.enabled && data?.apiKey) setLinearConfig(data); })
      .catch(() => {});
    api.getMondaySettings()
      .then(data => { if (data?.enabled && data?.apiKey) setMondayConfig(data); })
      .catch(() => {});
    api.getTrelloSettings()
      .then(data => { if (data?.enabled && data?.apiKey && data?.token) setTrelloConfig(data); })
      .catch(() => {});
    
    // Load Notion settings
    api.getNotionSettings()
      .then(data => { if (data?.enabled && data?.apiKey) setNotionConfig(data); })
      .catch(() => {});
    
    // Load Google Docs settings
    api.getGoogleDocsSettings()
      .then(data => { if (data?.enabled && data?.accessToken) setGoogleDocsConfig(data); })
      .catch(() => {});

    // Load Slack & Teams connected state from IndexedDB
    getSlackSettings().then(s => { if (s?.enabled && s?.accessToken) setSlackConnected(true); }).catch(() => {});
    getTeamsSettings().then(s => { if (s?.enabled && s?.accessToken) setTeamsConnected(true); }).catch(() => {});
    atlassianOAuth2.getState()
      .then(s => setConfluenceEnabled(s.isAuthenticated))
      .catch(() => {});
    linearOAuth2.getState()
      .then(s => setLinearOAuthEnabled(s.isAuthenticated))
      .catch(() => {});
    mondayOAuth2.getState()
      .then(s => setMondayOAuthEnabled(s.isAuthenticated))
      .catch(() => {});
  }, []);
  
  // Save/clear default repo
  const setAsDefaultRepo = useCallback((repoName: string | null) => {
    if (repoName) {
      localStorage.setItem('verigit-default-repo', repoName);
      setDefaultRepoName(repoName);
    } else {
      localStorage.removeItem('verigit-default-repo');
      setDefaultRepoName(null);
    }
  }, []);
  
  // Health check polling for connection status
  useEffect(() => {
    const checkHealth = async () => {
      try {
        const result = await api.checkHealth();
        setIsConnected(result.status === 'ok');
      } catch {
        setIsConnected(false);
      }
    };
    
    checkHealth();
    const interval = setInterval(checkHealth, 10000); // Check every 10 seconds
    return () => clearInterval(interval);
  }, []);
  

  // Auto-scroll console to bottom when logs change
  useEffect(() => {
    if (autoScroll && consoleRef.current) {
      consoleRef.current.scrollTop = consoleRef.current.scrollHeight;
    }
  }, [logs, autoScroll]);

  // Handle console scroll - pause auto-scroll when user scrolls up
  const handleConsoleScroll = useCallback(() => {
    if (!consoleRef.current) return;
    
    const { scrollTop, scrollHeight, clientHeight } = consoleRef.current;
    const isAtBottom = scrollHeight - scrollTop - clientHeight < 30;
    
    if (!isAtBottom) {
      // User scrolled up, pause auto-scroll
      setAutoScroll(false);
      
      // Clear any existing timeout
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
      
      // Resume auto-scroll after 3 seconds of no interaction
      scrollTimeoutRef.current = setTimeout(() => {
        setAutoScroll(true);
      }, 3000);
    } else {
      // User is at bottom, enable auto-scroll
      setAutoScroll(true);
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
    }
  }, []);

  // Close notification panel when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (showNotifications && !target.closest('[data-notification-panel]')) {
        setShowNotifications(false);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [showNotifications]);

  // Build config object for API calls
  // Uses stored login credentials automatically for Bitbucket/GitHub repos
  const buildConfig = useCallback((extra: Partial<RepoConfig> = {}): RepoConfig => {
    // Helper: inject stored auth credentials for remote repos
    // Always overrides existing creds — login credentials are the source of truth
    const injectStoredAuth = (config: RepoConfig): RepoConfig => {
      if (config.type === 'github' && (resolvedGhToken || githubToken)) {
        // GitHub uses PAT as Bearer token via accessToken field
        delete config.appPassword;
        config.accessToken = resolvedGhToken || githubToken || '';
        config.username = resolvedGhUsername || githubUsername || '';
      } else if (config.type === 'bitbucket' && (resolvedBbToken || storedToken)) {
        // Bitbucket API tokens use email:token with Basic auth
        delete config.accessToken;
        config.username = resolvedBbUsername || storedUsername || '';
        config.appPassword = resolvedBbToken || storedToken || '';
      } else if (config.type === 'gitlab' && resolvedGlToken) {
        // GitLab uses PAT as Bearer token via accessToken field
        delete config.appPassword;
        config.accessToken = resolvedGlToken;
      }
      return config;
    };

    // If using a predefined repo
    if (selectedPredefined) {
      const config: RepoConfig = {
        type: selectedPredefined.type,
        url: selectedPredefined.url,
        path: selectedPredefined.path,
        name: selectedPredefined.name,
        ...extra,
      };
      return injectStoredAuth(config);
    }
    
    // Manual configuration
    if (repoType === 'local') {
      return {
        type: 'local',
        path: localPath,
        ...extra,
      };
    }
    
    // GitHub or Bitbucket - always use stored login credentials
    const config: RepoConfig = {
      type: repoType,
      url: remoteUrl,
      ...extra,
    };
    
    return injectStoredAuth(config);
  }, [repoType, localPath, remoteUrl, selectedPredefined, storedUsername, storedToken, githubUsername, githubToken, resolvedBbEmail, resolvedBbToken, resolvedBbUsername, resolvedGhToken, resolvedGhUsername, resolvedGlToken]);

  // Auth is handled by stored login credentials, so we only need URL or path
  const canAnalyze = selectedPredefined 
    ? true 
    : repoType === 'local' 
      ? !!localPath 
      : !!remoteUrl;

  // Update current time every second
  useEffect(() => {
    const interval = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(interval);
  }, []);

  const fetchData = useCallback(async () => {
    if (!canAnalyze) return;
    
    setLoading(true);
    setError(null);
    setLogs(['Starting analysis...']);
    setShowConsole(true);
    const fetchStartTime = Date.now();
    const repoName = selectedPredefined?.name || localPath || remoteUrl || 'Unknown';
    audit.auditAnalysisStarted('git', { repoName });
    
    try {
      const config = buildConfig();
      
      // Calculate weeks from date range, or default to 4
      const weeksFromRange = dateRange?.from && dateRange?.to 
        ? Math.ceil((dateRange.to.getTime() - dateRange.from.getTime()) / (1000 * 60 * 60 * 24 * 7))
        : 4;
      
      const result = await api.analyzeRepo({
          ...config,
          weeks: weeksFromRange,
          startDate: dateRange?.from?.toISOString(),
          endDate: dateRange?.to?.toISOString(),
          sessionId,
        }, (line: string) => {
          setLogs(prev => [...prev, line]);
        });
      const data: any = { stats: result.stats, commits: result.commits };
      
      if ((data as any).logs) {
        setLogs((data as any).logs);
      }
      
      if ((data as any).error) throw new Error((data as any).error);
      
      setStats(data.stats);
      setCommits(data.commits);
      setLastRefresh(new Date());
      setLiveCommits([]);

      // Save to analysis history (Saved Analyses tab)
      const durationMs = Date.now() - fetchStartTime;
      audit.auditAnalysisCompleted('git', durationMs, { repoName, commitCount: data.commits?.length || 0, authorCount: data.stats?.totalAuthors || 0 });
      const snapId = historyStore.generateSnapshotId();
      historyStore.saveSnapshot(
        {
          id: snapId,
          mode: 'git',
          title: `Git — ${repoName}`,
          timestamp: new Date().toISOString(),
          durationMs,
          success: true,
          summary: {
            commitCount: data.commits?.length || 0,
            eventCount: 0,
            authorCount: data.stats?.totalAuthors || 0,
            repoCount: 1,
            dateRange: data.stats?.dateRange ? { start: data.stats.dateRange.start, end: data.stats.dateRange.end } : undefined,
          },
        },
        {
          stats: data.stats,
          commits: data.commits,
          repoName,
          dateRange: dateRange?.from && dateRange?.to
            ? { from: dateRange.from.toISOString(), to: dateRange.to.toISOString() }
            : undefined,
        } satisfies GitReviewData
      ).catch(err => console.error('[analysis-history] Failed to save git snapshot:', err));

      // Refresh history after analysis
      api.getHistory()
        .then(data => setHistory(data))
        .catch(err => console.error('Failed to refresh history:', err));

      notifyAnalysisComplete({
        commitCount: data.commits?.length || 0,
        eventCount: 0,
        durationMs,
        success: true,
      });
    } catch (e: unknown) {
      const errMsg = e instanceof Error ? e.message : 'Failed to fetch data';
      setError(errMsg);
      audit.auditAnalysisFailed('git', errMsg, { repoName });

      notifyAnalysisComplete({
        commitCount: 0,
        eventCount: 0,
        durationMs: Date.now() - fetchStartTime,
        success: false,
        error: errMsg,
      });
    } finally {
      setLoading(false);
    }
  }, [canAnalyze, buildConfig, dateRange, sessionId]);

  // Auto-analyze when default repo is selected on load
  useEffect(() => {
    if (selectedPredefined && defaultRepoName && !hasAutoLoaded && selectedPredefined.name === defaultRepoName) {
      setHasAutoLoaded(true);
      // Small delay to ensure everything is ready
      const timer = setTimeout(() => {
        fetchData();
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [selectedPredefined, defaultRepoName, hasAutoLoaded, fetchData]);

  // Silent refresh - fetches new data without showing console/loading state
  const silentRefresh = useCallback(async () => {
    if (!canAnalyze || loading || isAutoRefreshing) return;
    
    setIsAutoRefreshing(true);
    setLogs(prev => [...prev, `[${new Date().toISOString()}] Auto-refresh started...`]);
    
    try {
      const config = buildConfig();
      const weeksFromRange = dateRange?.from && dateRange?.to 
        ? Math.ceil((dateRange.to.getTime() - dateRange.from.getTime()) / (1000 * 60 * 60 * 24 * 7))
        : 4;
      
      const refreshResult = await api.analyzeRepo({
        ...config,
        weeks: weeksFromRange,
        startDate: dateRange?.from?.toISOString(),
        endDate: dateRange?.to?.toISOString(),
        sessionId,
      }, (line: string) => {
        setLogs(prev => [...prev, line]);
      });
      
      if (refreshResult.stats && refreshResult.commits) {
        // Check if data has changed by comparing latest commit hash
        const newLatestHash = refreshResult.commits[0]?.hash;
        const oldLatestHash = commits[0]?.hash;
        const hasNewCommits = newLatestHash !== oldLatestHash;
        
        // Always update stats and commits to reflect latest data
        setStats(refreshResult.stats);
        setCommits(refreshResult.commits);
        setLastAutoRefresh(new Date());
        
        // Add to live commits for notification if there are new ones
        if (hasNewCommits && refreshResult.commits.length > 0) {
          // Find how many new commits there are
          const oldHashes = new Set(commits.map(c => c.hash));
          const newOnes = refreshResult.commits.filter((c: Commit) => !oldHashes.has(c.hash));
          if (newOnes.length > 0) {
            setLiveCommits(prev => [...newOnes.map((c: Commit) => ({ ...c, isNew: true } as any)), ...prev].slice(0, 20));
            setLogs(prev => [...prev, `[${new Date().toISOString()}] Found ${newOnes.length} new commit(s)!`]);
          }
        }
        
        setLogs(prev => [...prev, `[${new Date().toISOString()}] Auto-refresh complete`]);
      }
    } catch (e) {
      console.error('Silent refresh failed:', e);
      setLogs(prev => [...prev, `[${new Date().toISOString()}] Auto-refresh failed: ${e}`]);
    } finally {
      setIsAutoRefreshing(false);
    }
  }, [canAnalyze, loading, isAutoRefreshing, buildConfig, dateRange, commits, sessionId]);

  // Auto-refresh interval
  useEffect(() => {
    if (!autoRefresh || !stats || !canAnalyze) return;
    
    const interval = setInterval(() => {
      silentRefresh();
    }, refreshInterval * 1000);
    
    return () => clearInterval(interval);
  }, [autoRefresh, stats, canAnalyze, refreshInterval, silentRefresh]);

  const fetchDiff = async (hash: string) => {
    setDiffLoading(true);
    try {
      const config = buildConfig({ hash });
      const data = await api.getCommitDiff(config, hash);
      setDiff(data.diff || 'No diff available');
    } catch {
      setDiff('Error loading diff');
    } finally {
      setDiffLoading(false);
    }
  };

  const handleCommitClick = (commit: Commit) => {
    setSelectedCommit(commit);
    setExpandedDiffFiles(new Set());
    fetchDiff(commit.hash);
  };

  // Get performance target status for an author
  const getAuthorTargetStatus = useCallback((author: AuthorStats): { 
    hasTarget: boolean; 
    status: 'exceeded' | 'on_track' | 'at_risk' | 'behind' | null;
    progress: number;
    target: PerformanceTarget | null;
  } => {
    const authorTarget = allTargets.find(
      t => t.authorName.toLowerCase() === author.name.toLowerCase() && t.status === 'active'
    );
    
    if (!authorTarget) {
      return { hasTarget: false, status: null, progress: 0, target: null };
    }
    
    // Calculate progress for each metric
    const progressValues: number[] = [];
    
    if (authorTarget.targets.minCommits) {
      progressValues.push(Math.min(100, (author.commits / authorTarget.targets.minCommits) * 100));
    }
    if (authorTarget.targets.minActiveDays) {
      progressValues.push(Math.min(100, (author.activeDays / authorTarget.targets.minActiveDays) * 100));
    }
    if (authorTarget.targets.minAdditions) {
      progressValues.push(Math.min(100, (author.additions / authorTarget.targets.minAdditions) * 100));
    }
    
    const overallProgress = progressValues.length > 0 
      ? progressValues.reduce((a, b) => a + b, 0) / progressValues.length 
      : 100;
    
    let status: 'exceeded' | 'on_track' | 'at_risk' | 'behind';
    if (overallProgress >= 100) status = 'exceeded';
    else if (overallProgress >= 80) status = 'on_track';
    else if (overallProgress >= 50) status = 'at_risk';
    else status = 'behind';
    
    return { hasTarget: true, status, progress: overallProgress, target: authorTarget };
  }, [allTargets]);

  const downloadPDF = async () => {
    if (!stats) return;
    
    try {
      // Apply alias resolution to author stats before sending to PDF
      const aliases = getStoredGitAliases();
      const mergedAuthorMap = new Map<string, typeof stats.authors[0]>();
      for (const author of stats.authors) {
        const canonical = resolveGitAuthor(author.name, aliases);
        if (mergedAuthorMap.has(canonical)) {
          const existing = mergedAuthorMap.get(canonical)!;
          existing.commits += author.commits;
          existing.additions += author.additions;
          existing.deletions += author.deletions;
          existing.activeDays = Math.max(existing.activeDays, author.activeDays);
          if (new Date(author.firstCommit) < new Date(existing.firstCommit)) existing.firstCommit = author.firstCommit;
          if (new Date(author.lastCommit) > new Date(existing.lastCommit)) existing.lastCommit = author.lastCommit;
        } else {
          mergedAuthorMap.set(canonical, { ...author, name: canonical });
        }
      }
      const resolvedStats = {
        ...stats,
        authors: Array.from(mergedAuthorMap.values()),
        totalAuthors: mergedAuthorMap.size,
      };

      // Also resolve commit author names
      const resolvedCommits = commits.map(c => ({ ...c, author: resolveGitAuthor(c.author, aliases) }));

      generateAndDownloadReport('main', {
          repoName: selectedPredefined?.name || localPath || remoteUrl || 'Repository',
          repoType: repoType,
          generatedAt: new Date().toISOString(),
          stats: resolvedStats,
          commits: resolvedCommits,
          dateRange: {
            start: dateRange?.from?.toISOString() || stats.dateRange.start,
            end: dateRange?.to?.toISOString() || stats.dateRange.end,
          },
        });
    } catch (err) {
      console.error('Failed to download PDF:', err);
      setError('Failed to generate PDF report');
    }
  };

  const filteredCommits = authorFilter === 'all' 
    ? commits 
    : commits.filter(c => c.author === authorFilter);

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric'
    });
  };

  // Prepare chart data
  const authorChartData = stats?.authors.map(a => ({
    name: a.name.split(' ')[0],
    commits: a.commits,
    additions: a.additions,
    deletions: a.deletions,
  })) || [];

  const dailyChartData = stats?.dailyActivity.map(d => ({
    date: formatDate(d.date),
    commits: d.commits,
    additions: d.additions,
    deletions: d.deletions,
  })) || [];

  // Calculate productivity metrics
  const getProductivityScore = (author: AuthorStats) => {
    const commitScore = author.commits * 10;
    const linesScore = (author.additions + author.deletions) / 100;
    const consistencyScore = author.activeDays * 5;
    return Math.round(commitScore + linesScore + consistencyScore);
  };

  const parsedDiffFiles = useMemo(() => {
    if (!diff) return [];
    const files: { filename: string; additions: number; deletions: number; lines: string[] }[] = [];
    const rawLines = diff.split('\n');
    let current: typeof files[0] | null = null;

    for (const line of rawLines) {
      if (line.startsWith('diff --git')) {
        const match = line.match(/diff --git a\/(.*?) b\/(.*)/);
        const filename = match ? match[2] : line.replace('diff --git ', '');
        current = { filename, additions: 0, deletions: 0, lines: [] };
        files.push(current);
        current.lines.push(line);
      } else if (current) {
        current.lines.push(line);
        if (line.startsWith('+') && !line.startsWith('+++')) {
          current.additions++;
        } else if (line.startsWith('-') && !line.startsWith('---')) {
          current.deletions++;
        }
      }
    }
    return files;
  }, [diff]);

  const toggleDiffFile = useCallback((idx: number) => {
    setExpandedDiffFiles(prev => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx);
      else next.add(idx);
      return next;
    });
  }, []);

  const expandAllDiffFiles = useCallback(() => {
    setExpandedDiffFiles(new Set(parsedDiffFiles.map((_, i) => i)));
  }, [parsedDiffFiles]);

  const collapseAllDiffFiles = useCallback(() => {
    setExpandedDiffFiles(new Set());
  }, []);

  const handleReopenSnapshot = useCallback(async (snapshotId: string) => {
    try {
      const meta = await historyStore.getSnapshotMeta(snapshotId);
      if (!meta) { console.error('[reopen] Snapshot meta not found:', snapshotId); return; }
      const data = await historyStore.loadSnapshotData(snapshotId);
      if (!data) { console.error('[reopen] Snapshot data not found:', snapshotId); return; }

      closeAllViews();

      switch (meta.mode) {
        case 'combined':
          setCombinedReviewData(data as CombinedReviewData);
          activateView('combined', setShowCombinedView);
          break;
        case 'cross_project':
          setCrossProjectReviewData(data as CrossProjectReviewData);
          activateView('cross-project', setShowCrossProjectView);
          break;
        case 'jira':
          setJiraReviewData(data as JiraReviewData);
          activateView('jira', setShowJiraMode);
          break;
        case 'linear':
          setLinearReviewData(data as LinearReviewData);
          activateView('linear', setShowLinearMode);
          break;
        case 'trello':
          setTrelloReviewData(data as TrelloReviewData);
          activateView('trello', setShowTrelloMode);
          break;
        case 'confluence': {
          const cData = data as ConfluenceReviewData['activityData'];
          setConfluenceReviewData({
            activityData: cData,
            selectedSpace: (meta.summary.extra?.spaceKey as string) ?? '',
            customFrom: meta.summary.dateRange?.start ?? '',
            customTo: meta.summary.dateRange?.end ?? '',
          });
          activateView('confluence', setShowConfluenceMode);
          break;
        }
        case 'git': {
          const gitData = data as GitReviewData;
          setStats(gitData.stats);
          setCommits(gitData.commits);
          setLastRefresh(new Date());
          break;
        }
        case 'slack':
          setSlackReviewSnapshot(data as import('./services/messaging-types').MessagingSnapshot);
          activateView('slack', setShowSlackMode);
          break;
        case 'teams':
          setTeamsReviewSnapshot(data as import('./services/messaging-types').MessagingSnapshot);
          activateView('teams', setShowTeamsMode);
          break;
        default:
          console.warn('[reopen] Unsupported mode:', meta.mode);
      }
    } catch (err) {
      console.error('[reopen] Failed to reopen snapshot:', err);
    }
  }, [closeAllViews]);

  const refreshPlatformConfigs = useCallback(() => {
    api.getJiraSettings()
      .then(data => { if (data && data.domain) setJiraConfig(data); else setJiraConfig(null); })
      .catch(() => {
        try { const stored = localStorage.getItem('verigit-jira-settings'); if (stored) setJiraConfig(JSON.parse(stored)); } catch {}
      });
    api.getLinearSettings().then(data => { if (data?.enabled && data?.apiKey) setLinearConfig(data); else setLinearConfig(null); }).catch(() => {});
    api.getMondaySettings().then(data => { if (data?.enabled && data?.apiKey) setMondayConfig(data); else setMondayConfig(null); }).catch(() => {});
    api.getTrelloSettings().then(data => { if (data?.enabled && data?.apiKey && data?.token) setTrelloConfig(data); else setTrelloConfig(null); }).catch(() => {});
    api.getNotionSettings().then(data => { if (data?.enabled && data?.apiKey) setNotionConfig(data); else setNotionConfig(null); }).catch(() => {});
    api.getGoogleDocsSettings().then(data => { if (data?.enabled && data?.accessToken) setGoogleDocsConfig(data); else setGoogleDocsConfig(null); }).catch(() => {});
    getSlackSettings().then(s => setSlackConnected(!!(s?.enabled && s?.accessToken))).catch(() => {});
    getTeamsSettings().then(s => setTeamsConnected(!!(s?.enabled && s?.accessToken))).catch(() => {});
    // Load Confluence enabled state from settings
    api.getConfluenceSettings().then(s => {
      if (s?.enabled !== undefined) {
        setConfluenceEnabled(s.enabled);
      } else {
        // Fallback to authentication state for backward compatibility
        atlassianOAuth2.getState().then(authState => setConfluenceEnabled(authState.isAuthenticated)).catch(() => {});
      }
    }).catch(() => {});
    // Refresh git provider credentials
    api.getBitbucketSettings().then(s => {
      if (s?.email) { setResolvedBbEmail(s.email); setResolvedBbToken(s.token); setResolvedBbUsername(s.username || null); }
    }).catch(() => {});
    api.getGitHubSettings().then(s => {
      if (s?.token) { setResolvedGhToken(s.token); setResolvedGhUsername(s.username || null); }
    }).catch(() => {});
    api.getGitLabSettings().then(s => {
      if (s?.token) { setResolvedGlToken(s.token); setResolvedGlInstanceUrl(s.instanceUrl || 'https://gitlab.com'); }
    }).catch(() => {});
  }, []);

  // Build sidebar actions
  const sidebarActions = useMemo((): SidebarAction[] => {
    const actions: SidebarAction[] = [];

    // Home / Dashboard
    actions.push({
      id: 'dashboard', label: 'Dashboard', section: 'views',
      icon: <FontAwesomeIcon icon={faChartLine} className="h-4 w-4 text-indigo-500" />,
      onClick: () => { closeAllViews(); activateView('dashboard', setShowDashboardView); navigateToPage('dashboard'); },
      active: showDashboardView,
    });

    // Views section
    if (stats) {
      actions.push({
        id: 'tv-mode', label: 'TV Mode', section: 'views',
        icon: <FontAwesomeIcon icon={faTv} className="h-4 w-4 text-slate-400" />,
        onClick: () => { audit.auditViewOpened('tv_mode'); setShowLiveBoard(true); },
      });
      actions.push({
        id: 'pdf', label: 'Download PDF', section: 'views',
        icon: <FontAwesomeIcon icon={faDownload} className="h-4 w-4 text-slate-400" />,
        onClick: downloadPDF,
      });
    }
    actions.push({
      id: 'cross-project', label: 'Cross-Project', section: 'views',
      icon: <FontAwesomeIcon icon={faLayerGroup} className="h-4 w-4 text-violet-500" />,
      onClick: () => { closeAllViews(); audit.auditViewOpened('cross_project'); activateView('cross-project', setShowCrossProjectView); navigateToPage('cross-project'); },
      active: showCrossProjectView,
    });
    actions.push({
      id: 'single-repo', label: 'Single Repo Analysis', section: 'views',
      icon: <FontAwesomeIcon icon={faCodeCommit} className="h-4 w-4 text-sky-500" />,
      onClick: () => { closeAllViews(); navigateToPage('single-repo'); },
      active: !showCrossProjectView && !showSettings && !showJiraMode && !showTimelineView && !showLinearMode && !showMondayMode && !showTrelloMode && !showConfluenceMode && !showNotionMode && !showGoogleDocsMode && !showCombinedView && !showIndicatorEditor && !showAuditHistoryView && !showReportsView && !showChangelogView && !showRepoChangelogView && !showDashboardView && !showSlackMode && !showTeamsMode,
    });
    actions.push({
      id: 'repo-changelog', label: 'Repo Changelog', section: 'views',
      icon: <FontAwesomeIcon icon={faFileLines} className="h-4 w-4 text-teal-500" />,
      onClick: () => { closeAllViews(); activateView('repo-changelog', setShowRepoChangelogView); navigateToPage('repo-changelog'); },
      active: showRepoChangelogView,
    });
    actions.push({
      id: 'combined', label: 'Combined Mode', section: 'views',
      icon: <FontAwesomeIcon icon={faCodeMerge} className="h-4 w-4 text-emerald-500" />,
      onClick: () => { closeAllViews(); audit.auditViewOpened('combined'); activateView('combined', setShowCombinedView); navigateToPage('combined'); },
      active: showCombinedView,
    });

    // Platforms section
    if (jiraConfig?.enabled && jiraConfig?.domain) {
      actions.push({
        id: 'jira', label: 'Jira', section: 'platforms',
        icon: <FontAwesomeIcon icon={faJira} className="h-4 w-4 text-[#2684FF]" />,
        onClick: () => { closeAllViews(); audit.auditViewOpened('jira'); activateView('jira', setShowJiraMode); navigateToPage('jira'); },
        active: showJiraMode,
      });
      actions.push({
        id: 'roadmap', label: 'Roadmap', section: 'platforms',
        icon: <FontAwesomeIcon icon={faTimeline} className="h-4 w-4 text-indigo-400" />,
        onClick: () => { closeAllViews(); audit.auditViewOpened('roadmap'); activateView('roadmap', setShowTimelineView); navigateToPage('roadmap'); },
        active: showTimelineView,
      });
    }
    if (linearConfig?.enabled && linearConfig?.apiKey) {
      actions.push({
        id: 'linear', label: 'Linear', section: 'platforms',
        icon: <><img src="/brands/linear/dark.svg" className="h-4 w-4 dark:hidden" alt="" /><img src="/brands/linear/light.svg" className="h-4 w-4 hidden dark:block" alt="" /></>,
        onClick: () => { closeAllViews(); audit.auditViewOpened('linear'); activateView('linear', setShowLinearMode); navigateToPage('linear'); },
        active: showLinearMode,
      });
    }
    if (mondayConfig?.enabled && mondayConfig?.apiKey) {
      actions.push({
        id: 'monday', label: 'Monday.com', section: 'platforms',
        icon: <span className="w-4 h-4 flex-shrink-0 flex items-center justify-center"><img src="/brands/monday/dark.svg" className="w-4 h-auto" alt="" /></span>,
        onClick: () => { closeAllViews(); activateView('monday', setShowMondayMode); navigateToPage('monday'); },
        active: showMondayMode,
      });
    }
    if (confluenceEnabled) {
      actions.push({
        id: 'confluence', label: 'Confluence', section: 'platforms',
        icon: <FontAwesomeIcon icon={faConfluence} className="h-4 w-4 text-blue-400" />,
        onClick: () => { closeAllViews(); audit.auditViewOpened('confluence'); activateView('confluence', setShowConfluenceMode); navigateToPage('confluence'); },
        active: showConfluenceMode,
      });
    }
    if (notionConfig?.enabled && notionConfig?.apiKey) {
      actions.push({
        id: 'notion', label: 'Notion', section: 'platforms',
        icon: <FontAwesomeIcon icon={faNotion} className="h-4 w-4 text-gray-900 dark:text-white" />,
        onClick: () => { closeAllViews(); audit.auditViewOpened('notion'); activateView('notion', setShowNotionMode); navigateToPage('notion'); },
        active: showNotionMode,
      });
    }
    if (googleDocsConfig?.enabled && googleDocsConfig?.accessToken) {
      actions.push({
        id: 'google-docs', label: 'Google Docs', section: 'platforms',
        icon: <FontAwesomeIcon icon={faGoogle} className="h-4 w-4 text-blue-500" />,
        onClick: () => { closeAllViews(); audit.auditViewOpened('google-docs'); activateView('google-docs', setShowGoogleDocsMode); navigateToPage('google-docs'); },
        active: showGoogleDocsMode,
      });
    }
    if (slackConnected) {
      actions.push({
        id: 'slack', label: 'Slack', section: 'platforms',
        icon: <FontAwesomeIcon icon={faSlack} className="h-4 w-4 text-[#E01E5A]" />,
        onClick: () => { closeAllViews(); audit.auditViewOpened('slack'); activateView('slack', setShowSlackMode); navigateToPage('slack'); },
        active: showSlackMode,
      });
    }
    if (teamsConnected) {
      actions.push({
        id: 'teams', label: 'Microsoft Teams', section: 'platforms',
        icon: <FontAwesomeIcon icon={faMicrosoft} className="h-4 w-4 text-[#6264A7]" />,
        onClick: () => { closeAllViews(); audit.auditViewOpened('teams'); activateView('teams', setShowTeamsMode); navigateToPage('teams'); },
        active: showTeamsMode,
      });
    }
    if (trelloConfig?.enabled && trelloConfig?.apiKey) {
      actions.push({
        id: 'trello', label: 'Trello', section: 'platforms',
        icon: <FontAwesomeIcon icon={faTrello} className="h-4 w-4 text-sky-400" />,
        onClick: () => { closeAllViews(); audit.auditViewOpened('trello'); activateView('trello', setShowTrelloMode); navigateToPage('trello'); },
        active: showTrelloMode,
      });
    }

    // Tools section
    actions.push({
      id: 'audit', label: 'Audit Log', section: 'tools',
      icon: <FontAwesomeIcon icon={faClockRotateLeft} className="h-4 w-4 text-amber-500" />,
      onClick: () => {
        // Refresh audit log when opening view
        api.getAuditLog()
          .then(data => setAuditLog(data))
          .catch(err => console.error('Failed to load audit log:', err));
        closeAllViews(); activateView('audit', setShowAuditHistoryView); navigateToPage('audit');
      },
      active: showAuditHistoryView,
      badge: history.length > 0 ? history.length : undefined,
    });
    actions.push({
      id: 'reports', label: 'Reports & Analytics', section: 'tools',
      icon: <FontAwesomeIcon icon={faChartBar} className="h-4 w-4 text-blue-500" />,
      onClick: () => { closeAllViews(); audit.auditViewOpened('reports'); activateView('reports', setShowReportsView); navigateToPage('reports'); },
      active: showReportsView,
    });
    actions.push({
      id: 'indicator-editor', label: 'Indicators', section: 'tools',
      icon: <FontAwesomeIcon icon={faGauge} className="h-4 w-4 text-rose-500" />,
      onClick: () => { closeAllViews(); activateView('indicators', setShowIndicatorEditor); navigateToPage('indicators'); },
      active: showIndicatorEditor,
    });

    // System section
    actions.push({
      id: 'dark-mode',
      label: themeMode === 'system' ? 'System Theme' : themeMode === 'dark' ? 'Dark Mode' : 'Light Mode',
      section: 'system',
      icon: themeMode === 'system' ? <FontAwesomeIcon icon={faDesktop} className="h-4 w-4 text-slate-400" /> : themeMode === 'dark' ? <FontAwesomeIcon icon={faMoon} className="h-4 w-4 text-indigo-400" /> : <FontAwesomeIcon icon={faSun} className="h-4 w-4 text-amber-400" />,
      onClick: () => setThemeMode(prev => prev === 'system' ? 'light' : prev === 'light' ? 'dark' : 'system'),
      active: isDarkMode,
    });
    actions.push({
      id: 'rainbow-bg', label: showRainbowBg ? 'Rainbow: On' : 'Rainbow: Off', section: 'system',
      icon: <FontAwesomeIcon icon={faWandMagicSparkles} className="h-4 w-4 text-fuchsia-500" />,
      onClick: () => setShowRainbowBg(!showRainbowBg),
      active: showRainbowBg,
    });
    actions.push({
      id: 'changelog', label: 'Changelog', section: 'system',
      icon: <FontAwesomeIcon icon={faScroll} className="h-4 w-4 text-cyan-500" />,
      onClick: () => { closeAllViews(); activateView('changelog', setShowChangelogView); navigateToPage('changelog'); },
      active: showChangelogView,
    });
    actions.push({
      id: 'settings', label: 'Settings', section: 'system',
      icon: <FontAwesomeIcon icon={faGear} className="h-4 w-4 text-slate-400" />,
      onClick: () => { closeAllViews(); audit.auditViewOpened('settings'); activateView('settings', setShowSettings); navigateToPage('settings'); },
      active: showSettings,
    });
    if (onLogout) {
      actions.push({
        id: 'logout', label: 'Logout', section: 'system',
        icon: <FontAwesomeIcon icon={faArrowRightFromBracket} className="h-4 w-4 text-red-400" />,
        onClick: onLogout,
      });
    }

    return actions;
  }, [stats, jiraConfig, linearConfig, mondayConfig, trelloConfig, confluenceEnabled, showAuditHistoryView, showReportsView, showChangelogView, showRepoChangelogView, showRainbowBg, themeMode, isDarkMode, history.length, onLogout, downloadPDF, closeAllViews, showIndicatorEditor, showTimelineView, showConfluenceMode, showDashboardView, showCrossProjectView, showSettings, showJiraMode, showLinearMode, showMondayMode, showTrelloMode, showCombinedView, navigateToPage, activateView, notionConfig, googleDocsConfig, slackConnected, teamsConnected, showNotionMode, showGoogleDocsMode, showSlackMode, showTeamsMode]);

  const hasActiveView = showCrossProjectView || showSettings || showJiraMode || showTimelineView || showLinearMode || showMondayMode || showTrelloMode || showConfluenceMode || showNotionMode || showGoogleDocsMode || showSlackMode || showTeamsMode || showCombinedView || showIndicatorEditor || showAuditHistoryView || showReportsView || showChangelogView || showRepoChangelogView || showDashboardView;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-900 dark:to-slate-800 overflow-x-clip">
      <NotificationPermissionDialog isDark={isDarkMode} />
      {/* Sidebar */}
      <AppSidebar actions={sidebarActions} onCollapsedChange={setSidebarCollapsed} onMobileChange={setIsMobile} showRainbow={showRainbowBg} />

      {/* Full-page rainbow blob background (flag-controlled) */}
      {showRainbowBg && (
        <div className="fixed inset-0 pointer-events-none z-0">
          <AnimatedBlobs scale={2.5} />
        </div>
      )}

      {/* Header - frosted glass with rainbow blobs */}
      <header className="sticky top-0 z-30 transition-all duration-300 electron-drag" style={{ marginLeft: isMobile ? 0 : (sidebarCollapsed ? SIDEBAR_COLLAPSED_WIDTH : SIDEBAR_WIDTH) } as React.CSSProperties}>
        {/* Rainbow blobs behind frost */}
        <div className="absolute inset-0 overflow-hidden">
          {showRainbowBg && <AnimatedBlobs scale={0.8} />}
        </div>
        {/* Frosted glass layer */}
        <div className={`absolute inset-0 ${showRainbowBg ? 'bg-white/50 dark:bg-slate-950/60' : 'bg-white/70 dark:bg-slate-950/70'} backdrop-blur-xl border-b border-black/[0.06] dark:border-white/[0.06]`} />
        {/* Content */}
        <div className="relative px-6 py-3">
          <div className="flex items-center justify-end gap-4">
              
              {/* Live commits indicator - clickable */}
              {liveCommits.length > 0 && (
                <div className="relative" data-notification-panel>
                  <button
                    onClick={() => setShowNotifications(!showNotifications)}
                    className="flex items-center gap-2 px-3 py-1.5 bg-blue-100 dark:bg-blue-900/30 rounded-full hover:bg-blue-200 dark:hover:bg-blue-900/50 transition-colors"
                  >
                    <FontAwesomeIcon icon={faBell} className="h-3 w-3 text-blue-600" />
                    <span className="text-xs font-medium text-blue-700 dark:text-blue-300">
                      {liveCommits.length} new
                    </span>
                  </button>
                  
                  {/* Notification dropdown panel */}
                  {showNotifications && (
                    <div className="absolute right-0 top-full mt-2 w-96 bg-white/90 dark:bg-slate-900/90 backdrop-blur-2xl rounded-xl shadow-2xl border border-black/[0.08] dark:border-white/[0.08] z-50 max-h-[400px] flex flex-col">
                      <div className="p-3 border-b flex items-center justify-between bg-blue-50 dark:bg-blue-900/20 flex-shrink-0">
                        <div className="flex items-center gap-2">
                          <FontAwesomeIcon icon={faBell} className="h-4 w-4 text-blue-600" />
                          <span className="font-medium text-sm">New Commits</span>
                          <span className="text-xs bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 px-1.5 py-0.5 rounded-full">
                            {liveCommits.length}
                          </span>
                        </div>
                        <div className="flex items-center gap-1">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setLiveCommits([]);
                              setShowNotifications(false);
                            }}
                            className="text-xs text-muted-foreground hover:text-foreground px-2 py-1 rounded hover:bg-gray-100 dark:hover:bg-gray-800"
                          >
                            Clear all
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setShowNotifications(false);
                            }}
                            className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-800"
                          >
                            <FontAwesomeIcon icon={faXmark} className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                      <div className="overflow-y-auto flex-1 min-h-0">
                        {liveCommits.map((commit, i) => (
                          <div 
                            key={commit.hash} 
                            className={`p-3 border-b last:border-b-0 hover:bg-gray-50 dark:hover:bg-gray-800/50 cursor-pointer ${
                              i === 0 ? 'bg-blue-50/50 dark:bg-blue-900/10' : ''
                            }`}
                            onClick={() => {
                              // Find the full commit in commits array, or create a minimal one
                              const fullCommit = commits.find(c => c.hash === commit.hash || c.hash === commit.shortHash);
                              if (fullCommit) {
                                handleCommitClick(fullCommit);
                              } else {
                                // Fetch diff directly for this commit
                                setSelectedCommit({
                                  hash: commit.hash || commit.shortHash,
                                  author: commit.author,
                                  email: '',
                                  date: commit.date,
                                  message: commit.message,
                                  branch: '',
                                  additions: 0,
                                  deletions: 0,
                                });
                                fetchDiff(commit.hash || commit.shortHash);
                              }
                              setShowNotifications(false);
                            }}
                          >
                            <div className="flex items-start gap-3">
                              <code className="text-xs font-mono text-blue-600 bg-blue-100 dark:bg-blue-900/30 px-1.5 py-0.5 rounded flex-shrink-0">
                                {commit.shortHash || commit.hash?.slice(0, 7)}
                              </code>
                              <div className="flex-1 min-w-0">
                                <p className="text-sm truncate">{commit.message}</p>
                                <div className="flex items-center gap-2 mt-1 text-xs text-muted-foreground">
                                  <span>{commit.author}</span>
                                  <span>•</span>
                                  <span>{commit.date && timeAgo(commit.date)}</span>
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                      <div className="p-2 border-t bg-gray-50 dark:bg-gray-800/50 flex-shrink-0">
                        <Button
                          variant="outline"
                          size="sm"
                          className="w-full text-xs"
                          onClick={() => {
                            setLiveCommits([]);
                            setShowNotifications(false);
                            fetchData();
                          }}
                        >
                          <FontAwesomeIcon icon={faRotate} className="h-3 w-3 mr-2" />
                          Refresh & Clear
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              )}
              
              {/* Time display */}
              <div className="text-right hidden sm:block">
                <div className="text-sm font-medium">
                  {currentTime.toLocaleTimeString('en-US', { 
                    hour: '2-digit', 
                    minute: '2-digit',
                    second: '2-digit'
                  })}
                </div>
                <div className="text-xs text-muted-foreground">
                  {currentTime.toLocaleDateString('en-US', { 
                    weekday: 'short',
                    month: 'short', 
                    day: 'numeric' 
                  })}
                </div>
              </div>
              
              {/* Connection status */}
              <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full ${
                isConnected 
                  ? 'bg-green-100 dark:bg-green-900/30' 
                  : 'bg-red-100 dark:bg-red-900/30'
              }`}>
                {isConnected ? (
                  <>
                    <span className="relative flex h-2 w-2">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                      <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500"></span>
                    </span>
                    <span className="text-xs font-medium text-green-700 dark:text-green-300">Live</span>
                  </>
                ) : (
                  <>
                    <FontAwesomeIcon icon={faWifi} className="h-3 w-3 text-red-600" />
                    <span className="text-xs font-medium text-red-700 dark:text-red-300">Offline</span>
                  </>
                )}
              </div>
          </div>
        </div>
      </header>

      <main className={`transition-all duration-300 ${(showSettings || showCrossProjectView || showCombinedView || showIndicatorEditor || showAuditHistoryView || showReportsView || showChangelogView || showConfluenceMode || showJiraMode || showLinearMode || showMondayMode || showTrelloMode || showTimelineView || showDashboardView || showSlackMode || showTeamsMode || showNotionMode || showGoogleDocsMode || showRepoChangelogView) ? 'h-[calc(100vh-64px)] overflow-y-auto' : 'h-[calc(100vh-64px)] overflow-y-auto px-4 sm:px-6 lg:px-8 py-6'}`} style={{ marginLeft: isMobile ? 0 : (sidebarCollapsed ? SIDEBAR_COLLAPSED_WIDTH : SIDEBAR_WIDTH) }}>

        {/* ===== Inline Page Views ===== */}
        {mountedViews.has('dashboard') && (
          <div style={{ display: showDashboardView ? 'block' : 'none' }} className="h-full">
            <DashboardView isDarkMode={isDarkMode} onReopenSnapshot={handleReopenSnapshot} />
          </div>
        )}
        {mountedViews.has('cross-project') && (
          <div style={{ display: showCrossProjectView ? 'block' : 'none' }} className="h-full">
            <CrossProjectView
              inline
              availableRepos={[
                ...predefinedRepos.map(r => ({
                  id: r.id, name: r.name, type: r.type, url: r.url, path: r.path, icon: r.icon, description: r.description,
                })),
                ...bitbucketRepos.filter(br => !predefinedRepos.some(pr => pr.url === br.cloneUrl)).map(r => ({
                  id: r.fullName, name: r.name, type: 'bitbucket' as const, url: r.cloneUrl,
                  description: r.description || r.fullName, project: r.project || undefined, updatedOn: r.updatedOn,
                })),
                ...githubRepos.filter(gr => !predefinedRepos.some(pr => pr.url === gr.cloneUrl)).map(r => ({
                  id: r.fullName, name: r.name, type: 'github' as const, url: r.cloneUrl,
                  description: r.description || r.fullName, updatedOn: r.updatedAt,
                })),
                ...gitlabRepos.filter(gr => !predefinedRepos.some(pr => pr.url === gr.cloneUrl)).map(r => ({
                  id: r.fullName, name: r.name, type: 'gitlab' as const, url: r.cloneUrl,
                  description: r.description || r.fullName, updatedOn: r.updatedAt,
                })),
              ]}
              onClose={() => setShowCrossProjectView(false)}
              storedUsername={resolvedBbUsername || storedUsername}
              storedEmail={resolvedBbEmail || storedEmail}
              storedToken={resolvedBbToken || storedToken}
              githubUsername={resolvedGhUsername || githubUsername}
              githubToken={resolvedGhToken || githubToken}
              reviewData={crossProjectReviewData}
            />
          </div>
        )}

        {mountedViews.has('indicators') && (
          <div style={{ display: showIndicatorEditor ? 'block' : 'none' }} className="h-full">
            <IndicatorConfigEditor
              inline
              onClose={() => setShowIndicatorEditor(false)}
            />
          </div>
        )}

        {mountedViews.has('settings') && (
          <div style={{ display: showSettings ? 'block' : 'none' }} className="h-full">
            <SettingsView
              inline
              onClose={() => {
                setShowSettings(false);
                refreshPlatformConfigs();
              }}
              storedEmail={storedEmail}
              storedToken={storedToken}
              storedUsername={storedUsername}
              githubUsername={githubUsername}
              githubToken={githubToken}
              onLogin={onLogin}
              onNavigateToIndicatorEditor={() => { closeAllViews(); activateView('indicators', setShowIndicatorEditor); }}
              onSettingsChanged={refreshPlatformConfigs}
            />
          </div>
        )}

        {mountedViews.has('roadmap') && jiraConfig?.enabled && jiraConfig?.domain && (
          <div style={{ display: showTimelineView ? 'block' : 'none' }} className="h-full">
            <TimelineView
              inline
              onClose={() => setShowTimelineView(false)}
              jiraDomain={jiraConfig.domain}
              jiraEmail={jiraConfig.email}
              jiraApiToken={jiraConfig.apiToken}
              defaultProject={jiraConfig.defaultProjectKey}
              defaultBoardId={jiraConfig.defaultBoardId}
              showRainbow={showRainbowBg}
            />
          </div>
        )}

        {mountedViews.has('jira') && jiraConfig?.enabled && jiraConfig?.domain && (
          <div style={{ display: showJiraMode ? 'block' : 'none' }} className="h-full">
            <JiraActivityView
              inline
              onClose={() => setShowJiraMode(false)}
              jiraDomain={jiraConfig.domain}
              jiraEmail={jiraConfig.email}
              jiraApiToken={jiraConfig.apiToken}
              defaultProjectKey={jiraConfig.defaultProjectKey}
              defaultBoardId={jiraConfig.defaultBoardId}
              showLagIndicators
              priorityUsers={jiraConfig.priorityUsers}
              reviewData={jiraReviewData}
            />
          </div>
        )}

        {mountedViews.has('linear') && linearConfig?.enabled && linearConfig?.apiKey && (
          <div style={{ display: showLinearMode ? 'block' : 'none' }} className="h-full">
            <LinearActivityView inline onClose={() => setShowLinearMode(false)} apiKey={linearConfig.apiKey} reviewData={linearReviewData} />
          </div>
        )}

        {mountedViews.has('monday') && mondayConfig?.enabled && mondayConfig?.apiKey && (
          <div style={{ display: showMondayMode ? 'block' : 'none' }} className="h-full">
            <MondayActivityView inline onClose={() => setShowMondayMode(false)} apiKey={mondayConfig.apiKey} />
          </div>
        )}

        {mountedViews.has('trello') && trelloConfig?.enabled && trelloConfig?.apiKey && trelloConfig?.token && (
          <div style={{ display: showTrelloMode ? 'block' : 'none' }} className="h-full">
            <TrelloActivityView inline onClose={() => setShowTrelloMode(false)} apiKey={trelloConfig.apiKey} token={trelloConfig.token} reviewData={trelloReviewData} />
          </div>
        )}

        {mountedViews.has('confluence') && confluenceEnabled && (
          <div style={{ display: showConfluenceMode ? 'block' : 'none' }} className="h-full">
            <ConfluenceActivityView inline onClose={() => setShowConfluenceMode(false)} onOpenSettings={() => { closeAllViews(); activateView('settings', setShowSettings); navigateToPage('settings'); }} reviewData={confluenceReviewData} />
          </div>
        )}

        {mountedViews.has('notion') && notionConfig?.enabled && (
          <div style={{ display: showNotionMode ? 'block' : 'none' }} className="h-full">
            <NotionActivityView inline onClose={() => setShowNotionMode(false)} onOpenSettings={() => { closeAllViews(); activateView('settings', setShowSettings); navigateToPage('settings'); }} reviewData={undefined} />
          </div>
        )}

        {mountedViews.has('google-docs') && googleDocsConfig?.enabled && (
          <div style={{ display: showGoogleDocsMode ? 'block' : 'none' }} className="h-full">
            <GoogleDocsActivityView inline onClose={() => setShowGoogleDocsMode(false)} onOpenSettings={() => { closeAllViews(); activateView('settings', setShowSettings); navigateToPage('settings'); }} reviewData={undefined} />
          </div>
        )}

        {mountedViews.has('slack') && (
          <div style={{ display: showSlackMode ? 'block' : 'none' }} className="h-full">
            <SlackView inline onClose={() => { setShowSlackMode(false); setSlackReviewSnapshot(undefined); }} onOpenSettings={() => { closeAllViews(); activateView('settings', setShowSettings); navigateToPage('settings'); }} reviewSnapshot={slackReviewSnapshot} />
          </div>
        )}

        {mountedViews.has('teams') && (
          <div style={{ display: showTeamsMode ? 'block' : 'none' }} className="h-full">
            <TeamsView inline onClose={() => { setShowTeamsMode(false); setTeamsReviewSnapshot(undefined); }} onOpenSettings={() => { closeAllViews(); activateView('settings', setShowSettings); navigateToPage('settings'); }} reviewSnapshot={teamsReviewSnapshot} />
          </div>
        )}

        {mountedViews.has('audit') && (
          <div style={{ display: showAuditHistoryView ? 'block' : 'none' }} className="h-full">
            <AuditHistoryView
              inline
              onClose={() => setShowAuditHistoryView(false)}
              history={history}
              auditLog={auditLog}
              onRefresh={() => {
                api.getHistory().then(data => setHistory(data)).catch(() => {});
                api.getAuditLog().then(data => setAuditLog(data)).catch(() => {});
              }}
            />
          </div>
        )}

        {mountedViews.has('reports') && (
          <div style={{ display: showReportsView ? 'block' : 'none' }} className="h-full">
            <ReportsView
              inline
              onClose={() => setShowReportsView(false)}
              history={history}
              onReopenSnapshot={handleReopenSnapshot}
            />
          </div>
        )}

        {mountedViews.has('changelog') && (
          <div style={{ display: showChangelogView ? 'block' : 'none' }} className="h-full">
            <ChangelogView inline onClose={() => setShowChangelogView(false)} />
          </div>
        )}

        {mountedViews.has('repo-changelog') && (() => {
          // Build combined repo list for the picker
          const changelogRepos = [
            ...predefinedRepos.map(r => ({ id: r.id, name: r.name, type: r.type, url: r.url, path: r.path, accessToken: r.accessToken, username: r.username, appPassword: r.appPassword, source: 'saved' as const })),
            ...bitbucketRepos.filter(br => !predefinedRepos.some(pr => pr.url === br.cloneUrl)).map(r => ({ id: r.fullName, name: r.name, type: 'bitbucket' as RepoType, url: r.cloneUrl, path: undefined as string | undefined, username: resolvedBbEmail ?? undefined, appPassword: resolvedBbToken ?? undefined, source: 'bitbucket' as const })),
            ...githubRepos.filter(gr => !predefinedRepos.some(pr => pr.url === gr.cloneUrl)).map(r => ({ id: r.fullName, name: r.name, type: 'github' as RepoType, url: r.cloneUrl, path: undefined as string | undefined, accessToken: resolvedGhToken ?? undefined, source: 'github' as const })),
            ...gitlabRepos.filter(gr => !predefinedRepos.some(pr => pr.url === gr.cloneUrl)).map(r => ({ id: r.fullName, name: r.name, type: 'gitlab' as RepoType, url: r.cloneUrl, path: undefined as string | undefined, accessToken: resolvedGlToken ?? undefined, source: 'gitlab' as const })),
          ];
          // Commits for gap/generate tabs — only if the analysed repo matches selected
          const currentCfg = buildConfig();
          const changelogCommits = selectedChangelogRepo && commits.length > 0 &&
            (selectedChangelogRepo.url === currentCfg.url || selectedChangelogRepo.path === currentCfg.path)
            ? commits : [];

          const sourceIcon = (source: string, type: string) => {
            if (type === 'github' || source === 'github') return <FontAwesomeIcon icon={faGithub} className="h-3.5 w-3.5 text-slate-500 dark:text-slate-400" />;
            if (type === 'bitbucket' || source === 'bitbucket') return <FontAwesomeIcon icon={faBitbucket} className="h-3.5 w-3.5 text-blue-500" />;
            if (type === 'gitlab' || source === 'gitlab') return <FontAwesomeIcon icon={faGitlab} className="h-3.5 w-3.5 text-orange-500" />;
            return <FontAwesomeIcon icon={faFileLines} className="h-3.5 w-3.5 text-slate-400" />;
          };

          return (
            <div style={{ display: showRepoChangelogView ? 'block' : 'none' }} className="h-full overflow-auto">
              <div className="p-6 max-w-5xl mx-auto space-y-6">
                {/* Header */}
                <div className="flex items-center justify-between">
                  <div>
                    <h1 className="text-xl font-semibold">Repo Changelog</h1>
                    <p className="text-sm text-muted-foreground mt-0.5">Detect, analyse, generate, and AI-enrich your repository changelog</p>
                  </div>
                  {selectedChangelogRepo && (
                    <button onClick={() => setSelectedChangelogRepo(null)} className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1.5 transition-colors">
                      <FontAwesomeIcon icon={faChevronLeft} className="h-3 w-3" />Change repo
                    </button>
                  )}
                </div>

                {/* Repo picker */}
                {!selectedChangelogRepo ? (
                  <div>
                    {changelogRepos.length === 0 ? (
                      <Card className="text-center py-12">
                        <CardContent>
                          <FontAwesomeIcon icon={faFileLines} className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                          <h3 className="text-lg font-medium mb-2">No Repositories Found</h3>
                          <p className="text-muted-foreground text-sm">Add repositories via Settings or run a Single Repo Analysis first.</p>
                        </CardContent>
                      </Card>
                    ) : (
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                        {changelogRepos.map(repo => (
                          <button
                            key={repo.id}
                            onClick={() => setSelectedChangelogRepo(repo)}
                            className="text-left rounded-xl border border-gray-200 dark:border-slate-700/60 bg-white dark:bg-slate-800/60 hover:border-teal-400 dark:hover:border-teal-500 hover:bg-teal-50/40 dark:hover:bg-teal-500/10 transition-all p-4 group"
                          >
                            <div className="flex items-start gap-3">
                              <div className="mt-0.5 w-8 h-8 rounded-lg bg-slate-100 dark:bg-slate-700 flex items-center justify-center flex-shrink-0 group-hover:bg-teal-100 dark:group-hover:bg-teal-500/20 transition-colors">
                                {sourceIcon(repo.source, repo.type)}
                              </div>
                              <div className="min-w-0 flex-1">
                                <p className="text-sm font-medium text-gray-900 dark:text-white truncate">{repo.name}</p>
                                <p className="text-[11px] text-muted-foreground mt-0.5 truncate">{repo.url ?? repo.path ?? repo.type}</p>
                                {repo.url === currentCfg.url && commits.length > 0 && (
                                  <span className="mt-1.5 inline-flex items-center gap-1 text-[10px] font-medium text-teal-600 dark:text-teal-400">
                                    <FontAwesomeIcon icon={faCircleCheck} className="h-2.5 w-2.5" />{commits.length} commits analysed
                                  </span>
                                )}
                              </div>
                            </div>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                ) : (() => {
                  // Apply same injectStoredAuth logic as buildConfig — login credentials are source of truth
                  const clCfg: { type: RepoType; url?: string; path?: string; accessToken?: string; username?: string; appPassword?: string } = {
                    type: selectedChangelogRepo.type, url: selectedChangelogRepo.url, path: selectedChangelogRepo.path,
                  };
                  if (selectedChangelogRepo.type === 'github' && (resolvedGhToken || githubToken)) {
                    clCfg.accessToken = resolvedGhToken || githubToken || '';
                    clCfg.username = resolvedGhUsername || githubUsername || '';
                  } else if (selectedChangelogRepo.type === 'bitbucket' && (resolvedBbToken || storedToken)) {
                    clCfg.username = resolvedBbUsername || storedUsername || '';
                    clCfg.appPassword = resolvedBbToken || storedToken || '';
                  } else if (selectedChangelogRepo.type === 'gitlab' && resolvedGlToken) {
                    clCfg.accessToken = resolvedGlToken;
                  } else {
                    clCfg.accessToken = selectedChangelogRepo.accessToken;
                    clCfg.username = selectedChangelogRepo.username;
                    clCfg.appPassword = selectedChangelogRepo.appPassword;
                  }
                  return (
                    <RepoChangelogTab
                      commits={changelogCommits}
                      repoConfig={clCfg}
                      repoName={selectedChangelogRepo.name}
                      integrations={{
                        // Jira: API key OR Atlassian OAuth2 (confluenceEnabled = same token)
                        ...((jiraConfig?.enabled && jiraConfig.domain) || confluenceEnabled ? {
                          jira: { domain: jiraConfig?.domain, email: jiraConfig?.email, apiToken: jiraConfig?.apiToken, projectKey: jiraConfig?.defaultProjectKey },
                        } : {}),
                        // Linear: API key OR OAuth2 token in IndexedDB
                        ...((linearConfig?.enabled && linearConfig.apiKey) || linearOAuthEnabled ? {
                          linear: { apiKey: linearConfig?.apiKey },
                        } : {}),
                        // Monday: API key OR OAuth2 token in IndexedDB
                        ...((mondayConfig?.enabled && mondayConfig.apiKey) || mondayOAuthEnabled ? {
                          monday: { apiKey: mondayConfig?.apiKey },
                        } : {}),
                        // Trello: API key + token only (OAuth 1.0a)
                        ...(trelloConfig?.enabled && trelloConfig.apiKey ? { trello: { apiKey: trelloConfig.apiKey, token: trelloConfig.token } } : {}),
                        // Confluence: Atlassian OAuth2 only
                        ...(confluenceEnabled ? { confluence: true } : {}),
                        // Notion: factory reads from IndexedDB
                        ...(notionConfig?.enabled && notionConfig.apiKey ? { notion: { apiKey: notionConfig.apiKey } } : {}),
                        // Google Docs: factory reads OAuth token from IndexedDB
                        ...(googleDocsConfig?.enabled && googleDocsConfig.accessToken ? { googleDocs: { accessToken: googleDocsConfig.accessToken } } : {}),
                      }}
                    />
                  );
                })()}
              </div>
            </div>
          );
        })()}

        {mountedViews.has('combined') && (
          <div style={{ display: showCombinedView ? 'block' : 'none' }} className="h-full">
            <CombinedProjectView
              inline
              availableRepos={[
                ...predefinedRepos.map(r => ({
                  id: r.id, name: r.name, type: r.type, url: r.url, path: r.path, icon: r.icon, description: r.description,
                })),
                ...bitbucketRepos.filter(br => !predefinedRepos.some(pr => pr.url === br.cloneUrl)).map(r => ({
                  id: r.fullName, name: r.name, type: 'bitbucket' as const, url: r.cloneUrl,
                  description: r.description || r.fullName, project: r.project || undefined, updatedOn: r.updatedOn,
                })),
                ...githubRepos.filter(gr => !predefinedRepos.some(pr => pr.url === gr.cloneUrl)).map(r => ({
                  id: r.fullName, name: r.name, type: 'github' as const, url: r.cloneUrl,
                  description: r.description || r.fullName, updatedOn: r.updatedAt,
                })),
                ...gitlabRepos.filter(gr => !predefinedRepos.some(pr => pr.url === gr.cloneUrl)).map(r => ({
                  id: r.fullName, name: r.name, type: 'gitlab' as const, url: r.cloneUrl,
                  description: r.description || r.fullName, updatedOn: r.updatedAt,
                })),
              ]}
              onClose={() => setShowCombinedView(false)}
              storedUsername={resolvedBbUsername || storedUsername}
              storedEmail={resolvedBbEmail || storedEmail}
              storedToken={resolvedBbToken || storedToken}
              jiraDomain={jiraConfig?.domain}
              jiraEmail={jiraConfig?.email}
              jiraApiToken={jiraConfig?.apiToken}
              defaultProjectKey={jiraConfig?.defaultProjectKey}
              defaultBoardId={jiraConfig?.defaultBoardId}
              reviewData={combinedReviewData}
              confluenceEnabled={confluenceEnabled}
            />
          </div>
        )}

      {/* ===== Dashboard Content (hidden when a view is active) ===== */}
      <div style={{ display: !hasActiveView ? 'block' : 'none' }}>
        {/* Live Commits Feed */}
        {/* {liveCommits.length > 0 && (
          <Card className="mb-6 border-blue-200 bg-blue-50/50 dark:bg-blue-900/10">
            <CardHeader className="py-3">
              <CardTitle className="text-sm flex items-center gap-2">
                <FontAwesomeIcon icon={faBell} className="h-4 w-4 text-blue-600" />
                New Commits Detected
                <span className="text-xs font-normal text-muted-foreground">
                  (watching for changes)
                </span>
              </CardTitle>
            </CardHeader>
            <CardContent className="py-0 pb-3">
              <div className="space-y-2 max-h-[150px] overflow-y-auto">
                {liveCommits.map((commit, i) => (
                  <div key={commit.hash} className={`flex items-center gap-3 p-2 rounded ${i === 0 ? 'bg-blue-100 dark:bg-blue-900/30' : ''}`}>
                    <div className="flex-shrink-0 w-16 font-mono text-xs text-blue-600">{commit.shortHash}</div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm truncate">{commit.message}</div>
                      <div className="text-xs text-muted-foreground">{commit.author}</div>
                    </div>
                    <div className="text-xs text-muted-foreground">{timeAgo(new Date(commit.date))}</div>
                  </div>
                ))}
              </div>
              <Button 
                variant="ghost" 
                size="sm" 
                className="w-full mt-2"
                onClick={() => {
                  setLiveCommits([]);
                  fetchData();
                }}
              >
                <FontAwesomeIcon icon={faRotate} className="h-3 w-3 mr-2" />
                Refresh to include new commits
              </Button>
            </CardContent>
          </Card>
        )} */}

        {/* ===== Bitbucket Repositories ===== */}
        {(bitbucketRepos.length > 0 || bitbucketReposLoading) && (
          <div className="mb-6 rounded-2xl bg-white/60 dark:bg-white/[0.03] backdrop-blur-xl border border-black/[0.06] dark:border-white/[0.06] overflow-hidden">
            <button
              onClick={() => setBitbucketCollapsed(!bitbucketCollapsed)}
              className="w-full flex items-center justify-between px-5 py-4 hover:bg-black/[0.02] dark:hover:bg-white/[0.02] transition-colors"
            >
              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-xl bg-blue-500/10 dark:bg-blue-400/10 flex items-center justify-center">
                  <FontAwesomeIcon icon={faCloud} className="h-4 w-4 text-blue-500 dark:text-blue-400" />
                </div>
                <span className="font-semibold text-[15px] text-gray-800 dark:text-gray-100">Bitbucket Repositories</span>
                {!bitbucketReposLoading && (
                  <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-blue-500/10 dark:bg-blue-400/10 text-blue-600 dark:text-blue-300">{bitbucketRepos.length}</span>
                )}
              </div>
              <div className="flex items-center gap-3">
                {bitbucketRepos.length > 5 && !bitbucketCollapsed && (
                  <div className="relative" onClick={(e) => e.stopPropagation()}>
                    <FontAwesomeIcon icon={faMagnifyingGlass} className="h-3.5 w-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                    <input
                      type="text"
                      value={bitbucketSearch}
                      onChange={(e) => setBitbucketSearch(e.target.value)}
                      placeholder="Search..."
                      className="pl-8 pr-3 py-1.5 text-sm rounded-xl bg-black/[0.04] dark:bg-white/[0.06] border-0 w-48 focus:outline-none focus:ring-2 focus:ring-blue-500/30 placeholder:text-gray-400"
                    />
                  </div>
                )}
                <FontAwesomeIcon icon={faChevronDown} className={`h-4 w-4 text-gray-400 transition-transform duration-200 ${bitbucketCollapsed ? '-rotate-90' : ''}`} />
              </div>
            </button>
            <AnimatePresence initial={false}>
              {!bitbucketCollapsed && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.2 }}
                  className="overflow-hidden"
                >
                  <div className="px-5 pb-5">
                    {bitbucketReposLoading ? (
                      <div className="flex items-center gap-2 text-gray-400 py-8 justify-center">
                        <FontAwesomeIcon icon={faSpinner} className="h-5 w-5 animate-spin" />
                        <span className="text-sm">Loading your repositories...</span>
                      </div>
                    ) : bitbucketReposError ? (
                      <div className="flex items-center gap-2 text-red-400 py-4">
                        <FontAwesomeIcon icon={faTriangleExclamation} className="h-4 w-4" />
                        <span className="text-sm">{bitbucketReposError}</span>
                      </div>
                    ) : (
                      <div className="grid gap-2.5" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))' }}>
                        {bitbucketRepos
                          .filter(r => !bitbucketSearch || 
                            r.name.toLowerCase().includes(bitbucketSearch.toLowerCase()) ||
                            r.fullName.toLowerCase().includes(bitbucketSearch.toLowerCase()) ||
                            (r.project || '').toLowerCase().includes(bitbucketSearch.toLowerCase())
                          )
                          .map((repo) => {
                            const isSelected = selectedPredefined?.url === repo.cloneUrl;
                            return (
                              <div
                                key={repo.fullName}
                                className={`cursor-pointer rounded-xl p-3 transition-all duration-150 border ${
                                  isSelected
                                    ? 'bg-blue-500/10 dark:bg-blue-400/10 border-blue-500/30 dark:border-blue-400/20 shadow-sm'
                                    : 'bg-white/50 dark:bg-white/[0.02] border-black/[0.04] dark:border-white/[0.04] hover:bg-white/80 dark:hover:bg-white/[0.04] hover:border-black/[0.08] dark:hover:border-white/[0.08] hover:shadow-sm'
                                }`}
                                onClick={() => {
                                  if (isSelected) { setSelectedPredefined(null); }
                                  else {
                                    clearAnalysisState();
                                    setSelectedPredefined({ id: repo.fullName, name: repo.name, description: repo.description || repo.fullName, type: 'bitbucket', url: repo.cloneUrl });
                                    setRepoType('bitbucket');
                                  }
                                }}
                              >
                                <div className="flex items-center gap-2 mb-1.5">
                                  <span className="text-base">🪣</span>
                                  <span className="font-medium text-[13px] text-gray-800 dark:text-gray-100 truncate">{repo.name}</span>
                                </div>
                                {repo.project && (
                                  <p className="text-[11px] text-gray-500 dark:text-gray-400 truncate mb-1.5">{repo.project}</p>
                                )}
                                <div className="flex items-center gap-1.5 flex-wrap">
                                  {repo.isPrivate ? (
                                    <span className="inline-flex items-center gap-0.5 text-[10px] px-1.5 py-0.5 rounded-full bg-amber-500/10 text-amber-600 dark:text-amber-300">
                                      <FontAwesomeIcon icon={faLock} className="h-2.5 w-2.5" /> Private
                                    </span>
                                  ) : (
                                    <span className="inline-flex items-center gap-0.5 text-[10px] px-1.5 py-0.5 rounded-full bg-emerald-500/10 text-emerald-600 dark:text-emerald-300">
                                      <FontAwesomeIcon icon={faGlobe} className="h-2.5 w-2.5" /> Public
                                    </span>
                                  )}
                                  {repo.language && (
                                    <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-gray-500/10 text-gray-500 dark:text-gray-400">{repo.language}</span>
                                  )}
                                  <span className="text-[10px] text-gray-400 ml-auto" title={repo.updatedOn}>{timeAgo(repo.updatedOn)}</span>
                                </div>
                              </div>
                            );
                          })}
                      </div>
                    )}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        )}

        {/* ===== GitHub Repositories ===== */}
        {(githubRepos.length > 0 || githubReposLoading) && (
          <div className="mb-6 rounded-2xl bg-white/60 dark:bg-white/[0.03] backdrop-blur-xl border border-black/[0.06] dark:border-white/[0.06] overflow-hidden">
            <button
              onClick={() => setGithubCollapsed(!githubCollapsed)}
              className="w-full flex items-center justify-between px-5 py-4 hover:bg-black/[0.02] dark:hover:bg-white/[0.02] transition-colors"
            >
              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-xl bg-gray-500/10 dark:bg-gray-400/10 flex items-center justify-center">
                  <FontAwesomeIcon icon={faGithub} className="h-4 w-4 text-gray-600 dark:text-gray-300" />
                </div>
                <span className="font-semibold text-[15px] text-gray-800 dark:text-gray-100">GitHub Repositories</span>
                {!githubReposLoading && (
                  <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-gray-500/10 dark:bg-gray-400/10 text-gray-600 dark:text-gray-300">{githubRepos.length}</span>
                )}
              </div>
              <div className="flex items-center gap-3">
                {githubRepos.length > 5 && !githubCollapsed && (
                  <div className="relative" onClick={(e) => e.stopPropagation()}>
                    <FontAwesomeIcon icon={faMagnifyingGlass} className="h-3.5 w-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                    <input
                      type="text"
                      value={githubSearch}
                      onChange={(e) => setGithubSearch(e.target.value)}
                      placeholder="Search..."
                      className="pl-8 pr-3 py-1.5 text-sm rounded-xl bg-black/[0.04] dark:bg-white/[0.06] border-0 w-48 focus:outline-none focus:ring-2 focus:ring-gray-500/30 placeholder:text-gray-400"
                    />
                  </div>
                )}
                <FontAwesomeIcon icon={faChevronDown} className={`h-4 w-4 text-gray-400 transition-transform duration-200 ${githubCollapsed ? '-rotate-90' : ''}`} />
              </div>
            </button>
            <AnimatePresence initial={false}>
              {!githubCollapsed && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.2 }}
                  className="overflow-hidden"
                >
                  <div className="px-5 pb-5">
                    {githubReposLoading ? (
                      <div className="flex items-center gap-2 text-gray-400 py-8 justify-center">
                        <FontAwesomeIcon icon={faSpinner} className="h-5 w-5 animate-spin" />
                        <span className="text-sm">Loading your GitHub repositories...</span>
                      </div>
                    ) : githubReposError ? (
                      <div className="flex items-center gap-2 text-red-400 py-4">
                        <FontAwesomeIcon icon={faTriangleExclamation} className="h-4 w-4" />
                        <span className="text-sm">{githubReposError}</span>
                      </div>
                    ) : (
                      <div className="grid gap-2.5" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))' }}>
                        {githubRepos
                          .filter(r => !githubSearch || 
                            r.name.toLowerCase().includes(githubSearch.toLowerCase()) ||
                            r.fullName.toLowerCase().includes(githubSearch.toLowerCase()) ||
                            (r.owner || '').toLowerCase().includes(githubSearch.toLowerCase())
                          )
                          .map((repo) => {
                            const isSelected = selectedPredefined?.url === repo.cloneUrl;
                            return (
                              <div
                                key={repo.fullName}
                                className={`cursor-pointer rounded-xl p-3 transition-all duration-150 border ${
                                  isSelected
                                    ? 'bg-gray-500/10 dark:bg-gray-400/10 border-gray-500/30 dark:border-gray-400/20 shadow-sm'
                                    : 'bg-white/50 dark:bg-white/[0.02] border-black/[0.04] dark:border-white/[0.04] hover:bg-white/80 dark:hover:bg-white/[0.04] hover:border-black/[0.08] dark:hover:border-white/[0.08] hover:shadow-sm'
                                }`}
                                onClick={() => {
                                  if (isSelected) { setSelectedPredefined(null); }
                                  else {
                                    clearAnalysisState();
                                    setSelectedPredefined({ id: repo.fullName, name: repo.name, description: repo.description || repo.fullName, type: 'github', url: repo.cloneUrl });
                                    setRepoType('github');
                                  }
                                }}
                              >
                                <div className="flex items-center gap-2 mb-1.5">
                                  <FontAwesomeIcon icon={faGithub} className="h-4 w-4 text-gray-500 dark:text-gray-400" />
                                  <span className="font-medium text-[13px] text-gray-800 dark:text-gray-100 truncate">{repo.name}</span>
                                </div>
                                {repo.owner && (
                                  <p className="text-[11px] text-gray-500 dark:text-gray-400 truncate mb-1.5">{repo.owner}</p>
                                )}
                                <div className="flex items-center gap-1.5 flex-wrap">
                                  {repo.isPrivate ? (
                                    <span className="inline-flex items-center gap-0.5 text-[10px] px-1.5 py-0.5 rounded-full bg-amber-500/10 text-amber-600 dark:text-amber-300">
                                      <FontAwesomeIcon icon={faLock} className="h-2.5 w-2.5" /> Private
                                    </span>
                                  ) : (
                                    <span className="inline-flex items-center gap-0.5 text-[10px] px-1.5 py-0.5 rounded-full bg-emerald-500/10 text-emerald-600 dark:text-emerald-300">
                                      <FontAwesomeIcon icon={faGlobe} className="h-2.5 w-2.5" /> Public
                                    </span>
                                  )}
                                  {repo.language && (
                                    <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-gray-500/10 text-gray-500 dark:text-gray-400">{repo.language}</span>
                                  )}
                                  {repo.stargazersCount > 0 && (
                                    <span className="text-[10px] text-gray-400 flex items-center gap-0.5">
                                      <FontAwesomeIcon icon={faStar} className="h-2.5 w-2.5" /> {repo.stargazersCount}
                                    </span>
                                  )}
                                  <span className="text-[10px] text-gray-400 ml-auto" title={repo.updatedAt}>{timeAgo(repo.updatedAt)}</span>
                                </div>
                              </div>
                            );
                          })}
                      </div>
                    )}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        )}

        {/* ===== GitLab Repositories ===== */}
        {(gitlabRepos.length > 0 || gitlabReposLoading) && (
          <div className="mb-6 rounded-2xl bg-white/60 dark:bg-white/[0.03] backdrop-blur-xl border border-black/[0.06] dark:border-white/[0.06] overflow-hidden">
            <button
              onClick={() => setGitlabCollapsed(!gitlabCollapsed)}
              className="w-full flex items-center justify-between px-5 py-4 hover:bg-black/[0.02] dark:hover:bg-white/[0.02] transition-colors"
            >
              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-xl bg-orange-500/10 dark:bg-orange-400/10 flex items-center justify-center">
                  <span className="text-orange-500 dark:text-orange-400 font-bold text-sm">GL</span>
                </div>
                <span className="font-semibold text-[15px] text-gray-800 dark:text-gray-100">GitLab Repositories</span>
                {!gitlabReposLoading && (
                  <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-orange-500/10 dark:bg-orange-400/10 text-orange-600 dark:text-orange-300">{gitlabRepos.length}</span>
                )}
                <span className="text-[9px] px-1.5 py-0.5 rounded bg-violet-500/20 text-violet-500 dark:text-violet-400 border border-violet-500/30 font-semibold">BETA</span>
              </div>
              <div className="flex items-center gap-3">
                {gitlabRepos.length > 5 && !gitlabCollapsed && (
                  <div className="relative" onClick={(e) => e.stopPropagation()}>
                    <FontAwesomeIcon icon={faMagnifyingGlass} className="h-3.5 w-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                    <input
                      type="text"
                      value={gitlabSearch}
                      onChange={(e) => setGitlabSearch(e.target.value)}
                      placeholder="Filter..."
                      className="pl-8 pr-3 py-1.5 w-48 text-xs bg-white/80 dark:bg-slate-700/50 border border-gray-200 dark:border-slate-600/50 rounded-lg text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-orange-500/30"
                    />
                  </div>
                )}
                <FontAwesomeIcon icon={faChevronDown} className={`h-4 w-4 text-gray-400 transition-transform duration-200 ${gitlabCollapsed ? '-rotate-90' : ''}`} />
              </div>
            </button>
            <AnimatePresence initial={false}>
              {!gitlabCollapsed && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.2 }}
                  className="overflow-hidden"
                >
                  <div className="px-5 pb-5">
                    {gitlabReposLoading ? (
                      <div className="flex items-center gap-2 text-gray-400 py-8 justify-center">
                        <FontAwesomeIcon icon={faSpinner} className="h-5 w-5 animate-spin" />
                        <span className="text-sm">Loading your repositories...</span>
                      </div>
                    ) : gitlabReposError ? (
                      <div className="flex items-center gap-2 text-red-400 py-4">
                        <FontAwesomeIcon icon={faTriangleExclamation} className="h-4 w-4" />
                        <span className="text-sm">{gitlabReposError}</span>
                      </div>
                    ) : (
                      <div className="grid gap-2.5" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))' }}>
                        {gitlabRepos
                          .filter(r => !gitlabSearch ||
                            r.name.toLowerCase().includes(gitlabSearch.toLowerCase()) ||
                            r.fullName.toLowerCase().includes(gitlabSearch.toLowerCase()) ||
                            (r.namespace || '').toLowerCase().includes(gitlabSearch.toLowerCase())
                          )
                          .map(repo => {
                            const isSelected = selectedPredefined?.url === repo.cloneUrl;
                            return (
                              <div
                                key={repo.fullName}
                                className={`cursor-pointer rounded-xl p-3 transition-all duration-150 border ${
                                  isSelected
                                    ? 'bg-orange-500/10 dark:bg-orange-400/10 border-orange-500/30 dark:border-orange-400/20 shadow-sm'
                                    : 'bg-white/50 dark:bg-white/[0.02] border-black/[0.04] dark:border-white/[0.04] hover:bg-white/80 dark:hover:bg-white/[0.04] hover:border-black/[0.08] dark:hover:border-white/[0.08] hover:shadow-sm'
                                }`}
                                onClick={() => {
                                  if (isSelected) { setSelectedPredefined(null); }
                                  else {
                                    clearAnalysisState();
                                    setSelectedPredefined({ id: repo.fullName, name: repo.name, description: repo.description || repo.fullName, type: 'gitlab', url: repo.cloneUrl });
                                    setRepoType('gitlab');
                                  }
                                }}
                              >
                                <div className="flex items-center gap-2 mb-1.5">
                                  <FontAwesomeIcon icon={faGitlab} className="h-4 w-4 text-orange-500 dark:text-orange-400" />
                                  <span className="font-medium text-[13px] text-gray-800 dark:text-gray-100 truncate">{repo.name}</span>
                                </div>
                                {repo.namespace && (
                                  <p className="text-[11px] text-gray-500 dark:text-gray-400 truncate mb-1.5">{repo.namespace}</p>
                                )}
                                <div className="flex items-center gap-1.5 flex-wrap">
                                  {repo.isPrivate ? (
                                    <span className="inline-flex items-center gap-0.5 text-[10px] px-1.5 py-0.5 rounded-full bg-amber-500/10 text-amber-600 dark:text-amber-300">
                                      <FontAwesomeIcon icon={faLock} className="h-2.5 w-2.5" /> Private
                                    </span>
                                  ) : (
                                    <span className="inline-flex items-center gap-0.5 text-[10px] px-1.5 py-0.5 rounded-full bg-emerald-500/10 text-emerald-600 dark:text-emerald-300">
                                      <FontAwesomeIcon icon={faGlobe} className="h-2.5 w-2.5" /> Public
                                    </span>
                                  )}
                                  {repo.language && (
                                    <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-gray-500/10 text-gray-500 dark:text-gray-400">{repo.language}</span>
                                  )}
                                  {repo.starCount > 0 && (
                                    <span className="text-[10px] text-gray-400 flex items-center gap-0.5">
                                      <FontAwesomeIcon icon={faStar} className="h-2.5 w-2.5" /> {repo.starCount}
                                    </span>
                                  )}
                                  <span className="text-[10px] text-gray-400 ml-auto" title={repo.updatedAt}>{timeAgo(repo.updatedAt)}</span>
                                </div>
                              </div>
                            );
                          })}
                      </div>
                    )}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        )}

        {/* ===== Quick Access Repositories ===== */}
        {predefinedRepos.length > 0 && (
          <div className="mb-6 rounded-2xl bg-white/60 dark:bg-white/[0.03] backdrop-blur-xl border border-black/[0.06] dark:border-white/[0.06] overflow-hidden">
            <button
              onClick={() => setQuickAccessCollapsed(!quickAccessCollapsed)}
              className="w-full flex items-center justify-between px-5 py-4 hover:bg-black/[0.02] dark:hover:bg-white/[0.02] transition-colors"
            >
              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-xl bg-amber-500/10 dark:bg-amber-400/10 flex items-center justify-center">
                  <FontAwesomeIcon icon={faWandMagicSparkles} className="h-4 w-4 text-amber-500 dark:text-amber-400" />
                </div>
                <span className="font-semibold text-[15px] text-gray-800 dark:text-gray-100">Quick Access Repositories</span>
              </div>
              <FontAwesomeIcon icon={faChevronDown} className={`h-4 w-4 text-gray-400 transition-transform duration-200 ${quickAccessCollapsed ? '-rotate-90' : ''}`} />
            </button>
            <AnimatePresence initial={false}>
              {!quickAccessCollapsed && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.2 }}
                  className="overflow-hidden"
                >
                  <div className="px-5 pb-5">
                    <div className="grid gap-2.5" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))' }}>
                      {predefinedRepos.map((repo) => {
                        const isSelected = selectedPredefined?.id === repo.id;
                        return (
                          <div
                            key={repo.id}
                            className={`cursor-pointer rounded-xl p-3.5 transition-all duration-150 border ${
                              isSelected
                                ? 'bg-amber-500/10 dark:bg-amber-400/10 border-amber-500/30 dark:border-amber-400/20 shadow-sm'
                                : 'bg-white/50 dark:bg-white/[0.02] border-black/[0.04] dark:border-white/[0.04] hover:bg-white/80 dark:hover:bg-white/[0.04] hover:border-black/[0.08] dark:hover:border-white/[0.08] shadow-[0_1px_3px_rgba(0,0,0,0.04)] hover:shadow-sm'
                            }`}
                            onClick={() => {
                              if (selectedPredefined?.id !== repo.id) clearAnalysisState();
                              setSelectedPredefined(selectedPredefined?.id === repo.id ? null : repo);
                              setRepoType(repo.type);
                            }}
                          >
                            <div className="flex items-center gap-2 mb-1.5">
                              <span className="text-lg">{repo.icon || (repo.type === 'github' ? '🐙' : repo.type === 'bitbucket' ? '🪣' : '📁')}</span>
                              <span className="font-medium text-[13px] text-gray-800 dark:text-gray-100 truncate">{repo.name}</span>
                            </div>
                            <p className="text-[11px] text-gray-500 dark:text-gray-400 truncate mb-2">{repo.description || repo.url || repo.path}</p>
                            <div className="flex items-center gap-1.5">
                              {repo.type === 'github' && <FontAwesomeIcon icon={faGithub} className="h-3 w-3 text-gray-400" />}
                              {repo.type === 'bitbucket' && <FontAwesomeIcon icon={faBitbucket} className="h-3 w-3 text-gray-400" />}
                              {repo.type === 'local' && <FontAwesomeIcon icon={faFolderOpen} className="h-3 w-3 text-gray-400" />}
                              <span className="text-[11px] text-gray-400 capitalize">{repo.type}</span>
                              {(repo.accessToken || repo.username) && (
                                <span className="text-[11px] text-emerald-500 ml-auto">🔐</span>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        )}

        {/* ===== Controls ===== */}
        <div className="mb-6 rounded-2xl bg-white/60 dark:bg-white/[0.03] backdrop-blur-xl border border-black/[0.06] dark:border-white/[0.06] p-5">
            {/* Repo Type Toggle */}
            <div className="flex gap-2 mb-4">
              {selectedPredefined ? (
                <>
                  <button className="flex items-center gap-2 px-3.5 py-2 text-sm font-medium rounded-xl bg-gray-900 dark:bg-white/10 text-white transition-colors">
                    <FontAwesomeIcon icon={faWandMagicSparkles} className="h-4 w-4" />
                    Predefined
                  </button>
                  <button
                    onClick={() => { clearAnalysisState(); setRepoType('local'); setSelectedPredefined(null); }}
                    className="flex items-center gap-2 px-3.5 py-2 text-sm font-medium rounded-xl bg-black/[0.04] dark:bg-white/[0.06] text-gray-600 dark:text-gray-300 hover:bg-black/[0.08] dark:hover:bg-white/[0.1] transition-colors"
                  >
                    <FontAwesomeIcon icon={faFolderOpen} className="h-4 w-4" />
                    Local Path
                  </button>
                  <button
                    onClick={() => { clearAnalysisState(); setRepoType('github'); setSelectedPredefined(null); }}
                    className="flex items-center gap-2 px-3.5 py-2 text-sm font-medium rounded-xl bg-black/[0.04] dark:bg-white/[0.06] text-gray-600 dark:text-gray-300 hover:bg-black/[0.08] dark:hover:bg-white/[0.1] transition-colors"
                  >
                    <FontAwesomeIcon icon={faGithub} className="h-4 w-4" />
                    GitHub
                  </button>
                  <button
                    onClick={() => { clearAnalysisState(); setRepoType('bitbucket'); setSelectedPredefined(null); }}
                    className="flex items-center gap-2 px-3.5 py-2 text-sm font-medium rounded-xl bg-black/[0.04] dark:bg-white/[0.06] text-gray-600 dark:text-gray-300 hover:bg-black/[0.08] dark:hover:bg-white/[0.1] transition-colors"
                  >
                    <FontAwesomeIcon icon={faBitbucket} className="h-4 w-4" />
                    Bitbucket
                  </button>
                </>
              ) : (
                <>
                  <button
                    onClick={() => setRepoType('local')}
                    className={`flex items-center gap-2 px-3.5 py-2 text-sm font-medium rounded-xl transition-colors ${
                      repoType === 'local' ? 'bg-gray-900 dark:bg-white/10 text-white' : 'bg-black/[0.04] dark:bg-white/[0.06] text-gray-600 dark:text-gray-300 hover:bg-black/[0.08] dark:hover:bg-white/[0.1]'
                    }`}
                  >
                    <FontAwesomeIcon icon={faFolderOpen} className="h-4 w-4" />
                    Local Path
                  </button>
                  <button
                    onClick={() => setRepoType('github')}
                    className={`flex items-center gap-2 px-3.5 py-2 text-sm font-medium rounded-xl transition-colors ${
                      repoType === 'github' ? 'bg-gray-900 dark:bg-white/10 text-white' : 'bg-black/[0.04] dark:bg-white/[0.06] text-gray-600 dark:text-gray-300 hover:bg-black/[0.08] dark:hover:bg-white/[0.1]'
                    }`}
                  >
                    <FontAwesomeIcon icon={faGithub} className="h-4 w-4" />
                    GitHub
                  </button>
                  <button
                    onClick={() => setRepoType('bitbucket')}
                    className={`flex items-center gap-2 px-3.5 py-2 text-sm font-medium rounded-xl transition-colors ${
                      repoType === 'bitbucket' ? 'bg-gray-900 dark:bg-white/10 text-white' : 'bg-black/[0.04] dark:bg-white/[0.06] text-gray-600 dark:text-gray-300 hover:bg-black/[0.08] dark:hover:bg-white/[0.1]'
                    }`}
                  >
                    <FontAwesomeIcon icon={faBitbucket} className="h-4 w-4" />
                    Bitbucket
                  </button>
                </>
              )}
            </div>

            {!selectedPredefined && (
              <div className="flex flex-wrap gap-4 items-end">
                {repoType === 'local' ? (
                  <div className="flex-1 min-w-[300px]">
                    <label className="text-sm font-medium mb-2 block text-gray-700 dark:text-gray-200">Repository Path</label>
                    <input
                      type="text"
                      value={localPath}
                      onChange={(e) => setLocalPath(e.target.value)}
                      placeholder="/path/to/your/repo"
                      className="w-full px-3.5 py-2.5 rounded-xl bg-black/[0.04] dark:bg-white/[0.06] border-0 text-sm text-gray-800 dark:text-gray-100 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/30"
                    />
                  </div>
                ) : (
                  <>
                    <div className="flex-1 min-w-[300px]">
                      <label className="text-sm font-medium mb-2 block text-gray-700 dark:text-gray-200">
                        {repoType === 'github' ? 'GitHub' : 'Bitbucket'} Repository URL
                      </label>
                      <input
                        type="text"
                        value={remoteUrl}
                        onChange={(e) => setRemoteUrl(e.target.value)}
                        placeholder={repoType === 'github' ? 'https://github.com/owner/repo' : 'https://bitbucket.org/workspace/repo'}
                        className="w-full px-3.5 py-2.5 rounded-xl bg-black/[0.04] dark:bg-white/[0.06] border-0 text-sm text-gray-800 dark:text-gray-100 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/30"
                      />
                    </div>
                    {storedUsername && (
                      <div className="flex items-end pb-1">
                        <div className="flex items-center gap-2 px-3 py-2 bg-emerald-500/10 border border-emerald-500/20 rounded-xl">
                          <FontAwesomeIcon icon={faCircleCheck} className="h-4 w-4 text-green-600" />
                          <span className="text-xs text-green-700 dark:text-green-300">
                            Authenticated as <strong>{storedUsername}</strong>
                          </span>
                        </div>
                      </div>
                    )}
                  </>
                )}
                <div>
                  <label className="text-sm font-medium mb-2 block">Time Range</label>
                  <DateRangePicker
                    dateRange={dateRange}
                    onDateRangeChange={setDateRange}
                  />
                </div>
                <div className="flex gap-2">
                  <Button onClick={() => fetchData()} disabled={loading || !canAnalyze}>
                    {loading ? (
                      <FontAwesomeIcon icon={faRotate} className="h-4 w-4 animate-spin mr-2" />
                    ) : (
                      <FontAwesomeIcon icon={faRotate} className="h-4 w-4 mr-2" />
                    )}
                    {loading ? 'Analyzing...' : 'Analyze'}
                  </Button>
                </div>
              </div>
            )}

            {selectedPredefined && (
              <div className="flex flex-wrap items-end gap-4">
                <div className="flex-1 min-w-[250px]">
                  <div className="flex items-center gap-3">
                    <span className="text-2xl">{selectedPredefined.icon || '📁'}</span>
                    <div>
                      <div className="font-medium flex items-center gap-2">
                        {selectedPredefined.name}
                        {defaultRepoName === selectedPredefined.name && (
                          <span className="text-xs px-1.5 py-0.5 bg-primary/10 text-primary rounded">Default</span>
                        )}
                      </div>
                      <div className="text-xs text-muted-foreground truncate max-w-[300px]">{selectedPredefined.url || selectedPredefined.path}</div>
                    </div>
                  </div>
                </div>
                <div>
                  <label className="text-sm font-medium mb-2 block">Time Range</label>
                  <DateRangePicker
                    dateRange={dateRange}
                    onDateRangeChange={setDateRange}
                  />
                </div>
                <div className="flex gap-2">
                  <Button onClick={() => fetchData()} disabled={loading} className="h-10">
                    {loading ? (
                      <FontAwesomeIcon icon={faRotate} className="h-4 w-4 animate-spin mr-2" />
                    ) : (
                      <FontAwesomeIcon icon={faRotate} className="h-4 w-4 mr-2" />
                    )}
                    {loading ? 'Analyzing...' : 'Analyze'}
                  </Button>
                  <Button
                    variant="outline"
                    className="h-10"
                    onClick={() => setAsDefaultRepo(
                      defaultRepoName === selectedPredefined.name ? null : selectedPredefined.name
                    )}
                    title={defaultRepoName === selectedPredefined.name ? "Remove as default" : "Set as default (auto-analyze on load)"}
                  >
                    {defaultRepoName === selectedPredefined.name ? (
                      <FontAwesomeIcon icon={faStar} className="h-4 w-4 fill-yellow-400 text-yellow-400" />
                    ) : (
                      <FontAwesomeIcon icon={faStar} className="h-4 w-4" />
                    )}
                  </Button>
                </div>
              </div>
            )}

            {/* Auth info - credentials come from login */}
            {!selectedPredefined && (repoType === 'github' || repoType === 'bitbucket') && storedUsername && (
              <div className="mt-3 text-xs text-muted-foreground">
                Using your login credentials to authenticate with {repoType === 'github' ? 'GitHub' : 'Bitbucket'}.
              </div>
            )}

            {/* Live Progress Panel */}
            {loading && logs.length > 0 && (() => {
              // Parse the latest progress line
              const progressLine = [...logs].reverse().find(l => l.includes('[PROGRESS]'));
              const lastLog = logs[logs.length - 1] || '';
              let pct = 0;
              let processed = 0;
              let total = 0;
              let commitHash = '';
              let author = '';
              let message = '';
              
              if (progressLine) {
                const match = progressLine.match(/\[PROGRESS\] (\d+)% \((\d+)\/(\d+)\) \| (\S+) \| ([^|]+) \| (.+)/);
                if (match) {
                  pct = parseInt(match[1]);
                  processed = parseInt(match[2]);
                  total = parseInt(match[3]);
                  commitHash = match[4];
                  author = match[5].trim();
                  message = match[6].trim();
                }
              }
              
              // Determine current phase from logs
              let phase = 'Initializing...';
              if (lastLog.includes('Starting clone') || lastLog.includes('[git] Cloning') || lastLog.includes('[git] Receiving') || lastLog.includes('[git] remote:')) {
                phase = 'Cloning repository...';
              } else if (lastLog.includes('Fetching latest') || lastLog.includes('Fetch complete')) {
                phase = 'Fetching latest changes...';
              } else if (lastLog.includes('Found') && lastLog.includes('commits to process')) {
                phase = `Found ${total} commits — processing...`;
              } else if (progressLine) {
                phase = `Processing commits (${processed}/${total})`;
              } else if (lastLog.includes('Processed all')) {
                phase = 'Computing statistics...';
              }
              
              return (
                <div className="mt-4 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <FontAwesomeIcon icon={faSpinner} className="h-4 w-4 animate-spin text-blue-600" />
                      <span className="text-sm font-medium text-blue-700 dark:text-blue-300">{phase}</span>
                    </div>
                    {total > 0 && (
                      <span className="text-sm font-mono text-blue-600 dark:text-blue-400">{pct}%</span>
                    )}
                  </div>
                  
                  {total > 0 && (
                    <div className="w-full bg-blue-200 dark:bg-blue-900/50 rounded-full h-2 mb-3">
                      <div 
                        className="bg-blue-600 dark:bg-blue-400 h-2 rounded-full transition-all duration-300"
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                  )}
                  
                  {commitHash && (
                    <div className="flex items-center gap-3 text-xs">
                      <code className="px-1.5 py-0.5 bg-blue-100 dark:bg-blue-800/50 rounded font-mono text-blue-800 dark:text-blue-200">
                        {commitHash}
                      </code>
                      <span className="font-medium text-blue-700 dark:text-blue-300">{author}</span>
                      <span className="text-blue-600/70 dark:text-blue-400/70 truncate flex-1">{message}</span>
                    </div>
                  )}
                </div>
              );
            })()}

            {error && (
              <div className="mt-4 p-3 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 rounded-md flex items-center gap-2">
                <FontAwesomeIcon icon={faTriangleExclamation} className="h-4 w-4" />
                {error}
              </div>
            )}
            {stats && (
              <div className="mt-4 text-sm text-muted-foreground flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <FontAwesomeIcon icon={faCircleCheck} className="h-4 w-4 text-green-500" />
                  Last updated: {timeAgo(lastRefresh)}
                  {lastAutoRefresh && (
                    <span className="text-xs text-muted-foreground">
                      (auto-refreshed {timeAgo(lastAutoRefresh)})
                    </span>
                  )}
                  {(isAutoRefreshing || loading) && (
                    <button
                      onClick={() => setShowConsole(true)}
                      className="flex items-center gap-1 px-1.5 py-0.5 rounded hover:bg-blue-100 dark:hover:bg-blue-900/30 transition-colors"
                      title="Click to view console output"
                    >
                      <FontAwesomeIcon icon={faRotate} className="h-3 w-3 animate-spin text-blue-500" />
                      <span className="text-xs text-blue-500">View logs</span>
                    </button>
                  )}
                </div>
                <div className="flex items-center gap-3">
                  <div className="flex items-center gap-2">
                    <button
                      className={`text-xs px-2 py-1 rounded-full transition-colors ${
                        autoRefresh 
                          ? 'bg-green-100 dark:bg-green-500/15 text-green-700 dark:text-green-300 hover:bg-green-200 dark:hover:bg-green-500/25' 
                          : 'bg-gray-100 dark:bg-white/[0.06] text-gray-500 dark:text-slate-400 hover:bg-gray-200 dark:hover:bg-white/[0.1]'
                      }`}
                      onClick={() => setAutoRefresh(!autoRefresh)}
                      title={autoRefresh ? 'Disable auto-refresh' : 'Enable auto-refresh'}
                    >
                      {autoRefresh ? '● Live' : '○ Paused'}
                    </button>
                    {autoRefresh && (
                      <Select 
                        value={refreshInterval.toString()} 
                        onValueChange={(v) => setRefreshInterval(parseInt(v))}
                      >
                        <SelectTrigger className="h-6 w-[70px] text-xs">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="10">10s</SelectItem>
                          <SelectItem value="30">30s</SelectItem>
                          <SelectItem value="60">1m</SelectItem>
                          <SelectItem value="120">2m</SelectItem>
                          <SelectItem value="300">5m</SelectItem>
                        </SelectContent>
                      </Select>
                    )}
                  </div>
                </div>
              </div>
            )}
        </div>

        {/* Console Output */}
        {showConsole && logs.length > 0 && (
          <Card className="mb-6">
            <CardHeader className="py-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm flex items-center gap-2">
                  <FontAwesomeIcon icon={faTerminal} className="h-4 w-4" />
                  Console Output
                  {(loading || isAutoRefreshing) && <FontAwesomeIcon icon={faRotate} className="h-3 w-3 animate-spin" />}
                </CardTitle>
                <div className="flex items-center gap-1">
                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={() => setLogs([])}
                    title="Clear logs"
                  >
                    <span className="text-xs">Clear</span>
                  </Button>
                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={() => setShowConsole(false)}
                    title="Close console"
                  >
                    <FontAwesomeIcon icon={faXmark} className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent className="pt-0">
                <div 
                  ref={consoleRef}
                  onScroll={handleConsoleScroll}
                  className="bg-slate-900 text-slate-100 rounded-md p-3 font-mono text-xs max-h-[200px] overflow-y-auto scroll-smooth"
                >
                  {logs.map((line, i) => (
                    <div key={i} className={`${
                      line.includes('ERROR') ? 'text-red-400' : 
                      line.includes('successful') || line.includes('complete') ? 'text-green-400' :
                      line.includes('[git]') ? 'text-blue-400' : ''
                    }`}>
                      {line}
                    </div>
                  ))}
                </div>
                {!autoScroll && (
                  <div className="text-xs text-muted-foreground mt-2 flex items-center justify-between">
                    <span>Auto-scroll paused</span>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      className="h-6 text-xs"
                      onClick={() => {
                        setAutoScroll(true);
                        if (consoleRef.current) {
                          consoleRef.current.scrollTop = consoleRef.current.scrollHeight;
                        }
                      }}
                    >
                      Resume
                    </Button>
                  </div>
                )}
              </CardContent>
          </Card>
        )}

        {stats && (
          <>
            {/* Stats Overview */}
            <div className="grid grid-cols-3 md:grid-cols-6 gap-3 mb-6">
              <div className="rounded-xl border border-black/[0.06] dark:border-white/[0.06] bg-white/60 dark:bg-white/[0.03] backdrop-blur-xl p-4">
                <div className="flex items-center gap-2 mb-1">
                  <FontAwesomeIcon icon={faCodeCommit} className="h-4 w-4 text-blue-500 dark:text-blue-400" />
                  <span className="text-xs text-muted-foreground">Commits</span>
                </div>
                <div className="text-2xl font-bold">{formatNumber(stats.totalCommits)}</div>
              </div>
              <div className="rounded-xl border border-black/[0.06] dark:border-white/[0.06] bg-white/60 dark:bg-white/[0.03] backdrop-blur-xl p-4">
                <div className="flex items-center gap-2 mb-1">
                  <FontAwesomeIcon icon={faUsers} className="h-4 w-4 text-violet-500 dark:text-violet-400" />
                  <span className="text-xs text-muted-foreground">Contributors</span>
                </div>
                <div className="text-2xl font-bold">{stats.totalAuthors}</div>
              </div>
              <div className="rounded-xl border border-black/[0.06] dark:border-white/[0.06] bg-white/60 dark:bg-white/[0.03] backdrop-blur-xl p-4">
                <div className="flex items-center gap-2 mb-1">
                  <FontAwesomeIcon icon={faCodeBranch} className="h-4 w-4 text-indigo-500 dark:text-indigo-400" />
                  <span className="text-xs text-muted-foreground">Branches</span>
                </div>
                <div className="text-2xl font-bold">{stats.totalBranches}</div>
              </div>
              <div className="rounded-xl border border-black/[0.06] dark:border-white/[0.06] bg-white/60 dark:bg-white/[0.03] backdrop-blur-xl p-4">
                <div className="flex items-center gap-2 mb-1">
                  <FontAwesomeIcon icon={faArrowTrendUp} className="h-4 w-4 text-emerald-500 dark:text-emerald-400" />
                  <span className="text-xs text-muted-foreground">Additions</span>
                </div>
                <div className="text-2xl font-bold text-emerald-600 dark:text-emerald-400">+{formatNumber(stats.totalAdditions)}</div>
              </div>
              <div className="rounded-xl border border-black/[0.06] dark:border-white/[0.06] bg-white/60 dark:bg-white/[0.03] backdrop-blur-xl p-4">
                <div className="flex items-center gap-2 mb-1">
                  <FontAwesomeIcon icon={faArrowTrendDown} className="h-4 w-4 text-rose-500 dark:text-rose-400" />
                  <span className="text-xs text-muted-foreground">Deletions</span>
                </div>
                <div className="text-2xl font-bold text-rose-600 dark:text-rose-400">-{formatNumber(stats.totalDeletions)}</div>
              </div>
              <div className="rounded-xl border border-black/[0.06] dark:border-white/[0.06] bg-white/60 dark:bg-white/[0.03] backdrop-blur-xl p-4">
                <div className="flex items-center gap-2 mb-1">
                  <FontAwesomeIcon icon={faCalendarDays} className="h-4 w-4 text-amber-500 dark:text-amber-400" />
                  <span className="text-xs text-muted-foreground">Period</span>
                </div>
                <div className="text-sm font-semibold mt-0.5">
                  {formatDate(stats.dateRange.start)} - {formatDate(stats.dateRange.end)}
                </div>
              </div>
            </div>

            {/* Main Content Tabs */}
            <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
              <TabsList>
                <TabsTrigger value="overview">Overview</TabsTrigger>
                <TabsTrigger value="contributors">Contributors</TabsTrigger>
                <TabsTrigger value="commits">Commits</TabsTrigger>
                <TabsTrigger value="branches">Branches</TabsTrigger>
              </TabsList>

              <TabsContent value="overview" className="space-y-4">
                <div className="grid lg:grid-cols-2 gap-4">
                  {/* Commits by Author */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-lg">Commits by Contributor</CardTitle>
                      <CardDescription>Commit distribution across team members</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <ResponsiveContainer width="100%" height={300}>
                        <BarChart data={authorChartData}>
                          <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                          <XAxis dataKey="name" className="text-xs" />
                          <YAxis className="text-xs" />
                          <Tooltip 
                            contentStyle={{ 
                              backgroundColor: 'hsl(var(--card))',
                              border: '1px solid hsl(var(--border))',
                              borderRadius: '8px'
                            }}
                          />
                          <Bar dataKey="commits" fill="hsl(var(--primary))" radius={[4, 4, 0, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </CardContent>
                  </Card>

                  {/* Daily Activity */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-lg">Daily Activity</CardTitle>
                      <CardDescription>Commit activity over time</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <ResponsiveContainer width="100%" height={300}>
                        <AreaChart data={dailyChartData}>
                          <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                          <XAxis dataKey="date" className="text-xs" />
                          <YAxis className="text-xs" />
                          <Tooltip 
                            contentStyle={{ 
                              backgroundColor: 'hsl(var(--card))',
                              border: '1px solid hsl(var(--border))',
                              borderRadius: '8px'
                            }}
                          />
                          <Area 
                            type="monotone" 
                            dataKey="commits" 
                            stroke="hsl(var(--primary))" 
                            fill="hsl(var(--primary))" 
                            fillOpacity={0.2}
                          />
                        </AreaChart>
                      </ResponsiveContainer>
                    </CardContent>
                  </Card>

                  {/* Lines Changed */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-lg">Lines Changed by Contributor</CardTitle>
                      <CardDescription>Additions and deletions per contributor</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <ResponsiveContainer width="100%" height={300}>
                        <BarChart data={authorChartData} layout="vertical">
                          <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                          <XAxis type="number" className="text-xs" />
                          <YAxis dataKey="name" type="category" className="text-xs" width={80} />
                          <Tooltip 
                            contentStyle={{ 
                              backgroundColor: 'hsl(var(--card))',
                              border: '1px solid hsl(var(--border))',
                              borderRadius: '8px'
                            }}
                          />
                          <Bar dataKey="additions" fill="hsl(160, 60%, 45%)" name="Additions" />
                          <Bar dataKey="deletions" fill="hsl(0, 70%, 55%)" name="Deletions" />
                        </BarChart>
                      </ResponsiveContainer>
                    </CardContent>
                  </Card>

                  {/* Commit Share */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-lg">Commit Share</CardTitle>
                      <CardDescription>Percentage of commits by contributor</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <ResponsiveContainer width="100%" height={300}>
                        <PieChart>
                          <Pie
                            data={authorChartData}
                            dataKey="commits"
                            nameKey="name"
                            cx="50%"
                            cy="50%"
                            outerRadius={100}
                            label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                          >
                            {authorChartData.map((_, index) => (
                              <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                            ))}
                          </Pie>
                          <Tooltip 
                            contentStyle={{ 
                              backgroundColor: 'hsl(var(--card))',
                              border: '1px solid hsl(var(--border))',
                              borderRadius: '8px'
                            }}
                          />
                        </PieChart>
                      </ResponsiveContainer>
                    </CardContent>
                  </Card>
                </div>
              </TabsContent>

              <TabsContent value="contributors" className="space-y-4">
                <div className="grid gap-4" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))' }}>
                  {stats.authors.map((author, idx) => {
                    const targetStatus = getAuthorTargetStatus(author);
                    
                    return (
                      <Card 
                        key={author.name} 
                        className={`overflow-hidden cursor-pointer hover:bg-white/80 dark:hover:bg-white/[0.05] transition-all ${
                          targetStatus.hasTarget && targetStatus.status === 'behind' ? 'ring-2 ring-red-400/60' :
                          targetStatus.hasTarget && targetStatus.status === 'at_risk' ? 'ring-2 ring-amber-400/60' :
                          targetStatus.hasTarget && targetStatus.status === 'exceeded' ? 'ring-2 ring-green-400/60' :
                          ''
                        }`}
                        onClick={() => {
                          setSelectedAuthor(author);
                          setShowAuthorDialog(true);
                          // Fetch targets for this author
                          api.getTargetsByAuthor(author.name)
                            .then(data => setAuthorTargets(Array.isArray(data) ? data : []))
                            .catch(() => setAuthorTargets([]));
                        }}
                      >
                        <div 
                          className="h-2" 
                          style={{ backgroundColor: CHART_COLORS[idx % CHART_COLORS.length] }}
                        />
                        <CardHeader className="pb-2">
                          <div className="flex items-center justify-between">
                            <CardTitle className="text-lg flex items-center gap-2">
                              {author.name}
                              {targetStatus.hasTarget && (
                                <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                                  targetStatus.status === 'exceeded' ? 'bg-green-100 text-green-700 dark:bg-green-500/20 dark:text-green-300' :
                                  targetStatus.status === 'on_track' ? 'bg-blue-100 text-blue-700 dark:bg-blue-500/20 dark:text-blue-300' :
                                  targetStatus.status === 'at_risk' ? 'bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-300' :
                                  'bg-red-100 text-red-700 dark:bg-red-500/20 dark:text-red-300'
                                }`}>
                                  {targetStatus.status === 'exceeded' ? '✓ Exceeded' :
                                   targetStatus.status === 'on_track' ? '● On Track' :
                                   targetStatus.status === 'at_risk' ? '⚠ At Risk' :
                                   '✗ Behind'}
                                </span>
                              )}
                            </CardTitle>
                            <div className="px-2 py-1 bg-primary/10 rounded-full text-xs font-medium">
                              Score: {getProductivityScore(author)}
                            </div>
                          </div>
                          <CardDescription>{author.email}</CardDescription>
                        </CardHeader>
                        <CardContent>
                          {/* Performance target progress bar */}
                          {targetStatus.hasTarget && (
                            <div className="mb-4">
                              <div className="flex items-center justify-between text-xs mb-1">
                                <span className="text-muted-foreground">Target Progress</span>
                                <span className="font-medium">{Math.round(targetStatus.progress)}%</span>
                              </div>
                              <div className="h-2 bg-gray-200 dark:bg-white/[0.08] rounded-full overflow-hidden">
                                <div 
                                  className={`h-full rounded-full transition-all ${
                                    targetStatus.status === 'exceeded' ? 'bg-green-500' :
                                    targetStatus.status === 'on_track' ? 'bg-blue-500' :
                                    targetStatus.status === 'at_risk' ? 'bg-amber-500' :
                                    'bg-red-500'
                                  }`}
                                  style={{ width: `${Math.min(100, targetStatus.progress)}%` }}
                                />
                              </div>
                            </div>
                          )}
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <div className="text-2xl font-bold">{author.commits}</div>
                              <div className="text-xs text-muted-foreground">Commits</div>
                            </div>
                            <div>
                              <div className="text-2xl font-bold">{author.activeDays}</div>
                              <div className="text-xs text-muted-foreground">Active Days</div>
                            </div>
                            <div>
                              <div className="text-lg font-semibold text-green-600 dark:text-emerald-400">+{formatNumber(author.additions)}</div>
                              <div className="text-xs text-muted-foreground">Additions</div>
                            </div>
                            <div>
                              <div className="text-lg font-semibold text-red-600 dark:text-rose-400">-{formatNumber(author.deletions)}</div>
                              <div className="text-xs text-muted-foreground">Deletions</div>
                            </div>
                          </div>
                          <div className="mt-4 pt-4 border-t border-black/[0.06] dark:border-white/[0.06]">
                            <div className="flex items-center gap-2 text-xs text-muted-foreground">
                              <FontAwesomeIcon icon={faClock} className="h-3 w-3" />
                              <span>Last commit: {timeAgo(author.lastCommit)}</span>
                            </div>
                            <div className="flex items-center gap-2 text-xs text-muted-foreground mt-1">
                              <FontAwesomeIcon icon={faCode} className="h-3 w-3" />
                              <span>Avg: {author.avgCommitsPerDay.toFixed(2)} commits/day</span>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              </TabsContent>

              <TabsContent value="commits" className="space-y-4">
                <Card>
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <div>
                        <CardTitle className="text-lg">Commit History</CardTitle>
                        <CardDescription>Click on a commit to view the diff</CardDescription>
                      </div>
                      <Select value={authorFilter} onValueChange={setAuthorFilter}>
                        <SelectTrigger className="w-[200px]">
                          <SelectValue placeholder="Filter by author" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All Authors</SelectItem>
                          {stats.authors.map(a => (
                            <SelectItem key={a.name} value={a.name}>{a.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2 max-h-[600px] overflow-y-auto">
                      {filteredCommits.map(commit => (
                        <div
                          key={commit.hash}
                          onClick={() => handleCommitClick(commit)}
                          className="p-3 border border-black/[0.06] dark:border-white/[0.06] rounded-xl hover:bg-white/80 dark:hover:bg-white/[0.04] cursor-pointer transition-colors"
                        >
                          <div className="flex items-start justify-between gap-4">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                <code className="text-xs bg-black/[0.04] dark:bg-white/[0.06] px-1.5 py-0.5 rounded font-mono">
                                  {commit.hash.substring(0, 7)}
                                </code>
                                {commit.branch && (
                                  <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${
                                    commit.branch.startsWith('origin/') || commit.branch.startsWith('remotes/')
                                      ? 'bg-blue-100 dark:bg-blue-500/15 text-blue-700 dark:text-blue-300 border border-blue-200 dark:border-blue-500/30'
                                      : 'bg-purple-100 dark:bg-purple-500/15 text-purple-700 dark:text-purple-300 border border-purple-200 dark:border-purple-500/30'
                                  }`}>
                                    {commit.branch.replace(/^origin\//, '')}
                                  </span>
                                )}
                              </div>
                              <p className="font-medium mt-1 truncate">{commit.message}</p>
                              <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground">
                                <span className="flex items-center gap-1">
                                  <FontAwesomeIcon icon={faUsers} className="h-3 w-3" />
                                  {commit.author}
                                </span>
                                <span className="flex items-center gap-1">
                                  <FontAwesomeIcon icon={faClock} className="h-3 w-3" />
                                  {timeAgo(commit.date)}
                                </span>
                              </div>
                            </div>
                            <div className="flex items-center gap-2 text-xs shrink-0">
                              <span className="text-green-600 dark:text-emerald-400">+{commit.additions}</span>
                              <span className="text-red-600 dark:text-rose-400">-{commit.deletions}</span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="branches" className="space-y-4">
                <Card>
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <div>
                        <CardTitle className="text-lg">All Branches</CardTitle>
                        <CardDescription>
                          {stats.branches.filter(b => !b.isRemote).length} local, {stats.branches.filter(b => b.isRemote).length} remote
                        </CardDescription>
                      </div>
                      <div className="flex items-center gap-2">
                        <Select 
                          value={branchFilter} 
                          onValueChange={(v) => setBranchFilter(v as 'all' | 'local' | 'remote')}
                        >
                          <SelectTrigger className="w-[120px] h-8 text-xs">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="all">All</SelectItem>
                            <SelectItem value="local">Local only</SelectItem>
                            <SelectItem value="remote">Remote only</SelectItem>
                          </SelectContent>
                        </Select>
                        <Select 
                          value={branchSort} 
                          onValueChange={(v) => setBranchSort(v as 'recent' | 'name' | 'commits')}
                        >
                          <SelectTrigger className="w-[140px] h-8 text-xs">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="recent">Most Recent</SelectItem>
                            <SelectItem value="name">Name (A-Z)</SelectItem>
                            <SelectItem value="commits">Most Commits</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {(() => {
                        // Filter branches
                        let filtered = stats.branches.filter(b => {
                          if (branchFilter === 'local') return !b.isRemote;
                          if (branchFilter === 'remote') return b.isRemote;
                          return true;
                        });
                        
                        // Sort branches
                        filtered = [...filtered].sort((a, b) => {
                          if (branchSort === 'recent') {
                            return new Date(b.lastCommit).getTime() - new Date(a.lastCommit).getTime();
                          }
                          if (branchSort === 'name') {
                            return a.name.localeCompare(b.name);
                          }
                          return b.commitCount - a.commitCount;
                        });
                        
                        return filtered.map((branch, idx) => {
                          const branchCommits = branch.recentCommits || [];
                          const isStale = branch.lastCommit && 
                            (Date.now() - new Date(branch.lastCommit).getTime()) > 30 * 24 * 60 * 60 * 1000; // 30 days
                          
                          // Calculate all contributors for this branch
                          const authorCounts: Record<string, number> = {};
                          branchCommits.forEach(c => {
                            authorCounts[c.author] = (authorCounts[c.author] || 0) + 1;
                          });
                          const sortedContributors = Object.entries(authorCounts)
                            .sort((a, b) => b[1] - a[1])
                            .map(([name, count]) => ({
                              name,
                              count,
                              percentage: branchCommits.length > 0 
                                ? Math.round((count / branchCommits.length) * 100) 
                                : 0
                            }));
                          
                          return (
                            <div
                              key={branch.name}
                              className={`p-3 border border-black/[0.06] dark:border-white/[0.06] rounded-xl transition-all cursor-pointer ${
                                selectedBranch === branch.name 
                                  ? 'border-primary/40 bg-primary/5 dark:bg-primary/10' 
                                  : 'hover:bg-white/80 dark:hover:bg-white/[0.04]'
                              }`}
                              onClick={() => setSelectedBranch(selectedBranch === branch.name ? null : branch.name)}
                            >
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                  <div className={`p-1.5 rounded-full ${
                                    isStale 
                                      ? 'bg-amber-100 dark:bg-amber-900/30' 
                                      : 'bg-green-100 dark:bg-green-900/30'
                                  }`}>
                                    <FontAwesomeIcon icon={faCodeBranch} className={`h-4 w-4 ${
                                      isStale 
                                        ? 'text-amber-600 dark:text-amber-400' 
                                        : 'text-green-600 dark:text-green-400'
                                    }`} />
                                  </div>
                                  <div>
                                    <div className="font-medium flex items-center gap-2">
                                      {branch.name.replace(/^origin\//, '')}
                                      {branch.isRemote && (
                                        <span className="text-xs bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 px-1.5 py-0.5 rounded">
                                          remote
                                        </span>
                                      )}
                                      {isStale && (
                                        <span className="text-xs bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 px-1.5 py-0.5 rounded">
                                          stale
                                        </span>
                                      )}
                                      {idx === 0 && branchSort === 'recent' && (
                                        <span className="text-xs bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 px-1.5 py-0.5 rounded">
                                          latest
                                        </span>
                                      )}
                                    </div>
                                    {/* Contributors badges */}
                                    {sortedContributors.length > 0 && branchCommits.length >= 1 && (
                                      <div className="flex flex-wrap gap-1 mt-1">
                                        {sortedContributors.map((contributor, cIdx) => (
                                          <span 
                                            key={contributor.name}
                                            className={`text-xs px-1.5 py-0.5 rounded flex items-center gap-1 ${
                                              cIdx === 0 
                                                ? 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300' 
                                                : 'bg-gray-100 dark:bg-white/[0.06] text-gray-600 dark:text-gray-400'
                                            }`}
                                            title={`${contributor.name}: ${contributor.count} commits (${contributor.percentage}%)`}
                                          >
                                            {cIdx === 0 && <FontAwesomeIcon icon={faTrophy} className="h-3 w-3" />}
                                            {contributor.name.split(' ')[0]}
                                            <span className="opacity-70">({contributor.percentage}%)</span>
                                          </span>
                                        ))}
                                      </div>
                                    )}
                                    <div className="text-xs text-muted-foreground">
                                      Last commit by <span className="font-medium">{branch.lastAuthor}</span> • {branch.lastCommit && timeAgo(branch.lastCommit)}
                                    </div>
                                  </div>
                                </div>
                                <div className="flex items-center gap-4">
                                  <div className="text-right">
                                    <div className="text-sm font-medium">{formatNumber(branch.commitCount)}</div>
                                    <div className="text-xs text-muted-foreground">commits</div>
                                  </div>
                                  <FontAwesomeIcon icon={faChevronDown} className={`h-4 w-4 text-muted-foreground transition-transform ${
                                    selectedBranch === branch.name ? 'rotate-180' : ''
                                  }`} />
                                </div>
                              </div>
                              
                              {/* Expanded view with recent commits */}
                              {selectedBranch === branch.name && (
                                <div className="mt-3 pt-3 border-t">
                                  <div className="text-xs font-medium text-muted-foreground mb-2">
                                    Recent commits on this branch ({branchCommits.length} total)
                                  </div>
                                  {branchCommits.length > 0 ? (
                                    <div className="space-y-1.5">
                                      {branchCommits
                                        .slice(0, expandedBranchCommits.has(branch.name) ? undefined : 5)
                                        .map(commit => (
                                          <div 
                                            key={commit.hash}
                                            className="flex items-center gap-2 text-xs p-2 rounded bg-muted/50 hover:bg-muted cursor-pointer"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              handleCommitClick({
                                                ...commit,
                                                branch: branch.name,
                                                additions: 0,
                                                deletions: 0,
                                                isMerge: false,
                                              });
                                            }}
                                          >
                                            <code className="text-primary font-mono">{commit.hash.slice(0, 7)}</code>
                                            <span className="truncate flex-1">{commit.message}</span>
                                            <span className="text-muted-foreground">{timeAgo(commit.date)}</span>
                                          </div>
                                        ))}
                                      {branchCommits.length > 5 && !expandedBranchCommits.has(branch.name) && (
                                        <button
                                          className="w-full text-xs text-primary hover:text-primary/80 text-center py-2 rounded hover:bg-muted/50 transition-colors"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            setExpandedBranchCommits(prev => new Set([...prev, branch.name]));
                                          }}
                                        >
                                          Show {branchCommits.length - 5} more commits
                                        </button>
                                      )}
                                      {expandedBranchCommits.has(branch.name) && branchCommits.length > 5 && (
                                        <button
                                          className="w-full text-xs text-muted-foreground hover:text-foreground text-center py-2 rounded hover:bg-muted/50 transition-colors"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            setExpandedBranchCommits(prev => {
                                              const next = new Set(prev);
                                              next.delete(branch.name);
                                              return next;
                                            });
                                          }}
                                        >
                                          Show less
                                        </button>
                                      )}
                                    </div>
                                  ) : (
                                    <div className="text-xs text-muted-foreground italic">
                                      No commits in selected date range
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          );
                        });
                      })()}
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

            </Tabs>
          </>
        )}

        {!stats && !loading && (
          <Card className="text-center py-12">
            <CardContent>
              <FontAwesomeIcon icon={faFileLines} className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <h3 className="text-lg font-medium mb-2">No Repository Selected</h3>
              <p className="text-muted-foreground mb-4">
                Enter a path to a git repository and click Analyze to get started.
              </p>
            </CardContent>
          </Card>
        )}
        </div>
      </main>

      {/* Diff Modal */}
      <Dialog open={!!selectedCommit} onOpenChange={() => setSelectedCommit(null)}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <code className="text-sm bg-muted px-2 py-1 rounded">{selectedCommit?.hash}</code>
              <span className="truncate">{selectedCommit?.message}</span>
            </DialogTitle>
          </DialogHeader>
          <div className="flex-1 overflow-auto bg-muted/50 rounded-lg p-2">
            {diffLoading ? (
              <div className="flex items-center justify-center py-8">
                <FontAwesomeIcon icon={faRotate} className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            ) : parsedDiffFiles.length > 0 ? (
              <div className="space-y-1">
                {/* File summary bar */}
                <div className="flex items-center justify-between px-3 py-2 rounded-lg mb-2 bg-muted/60">
                  <span className="text-xs font-medium text-muted-foreground">
                    {parsedDiffFiles.length} file{parsedDiffFiles.length !== 1 ? 's' : ''} changed
                    <span className="ml-2 text-emerald-600 dark:text-emerald-400">
                      +{parsedDiffFiles.reduce((s, f) => s + f.additions, 0)}
                    </span>
                    <span className="ml-1 text-rose-600 dark:text-rose-400">
                      -{parsedDiffFiles.reduce((s, f) => s + f.deletions, 0)}
                    </span>
                  </span>
                  <div className="flex items-center gap-1">
                    <button
                      onClick={expandAllDiffFiles}
                      className="text-xs px-2 py-0.5 rounded hover:bg-muted text-muted-foreground"
                    >
                      Expand all
                    </button>
                    <button
                      onClick={collapseAllDiffFiles}
                      className="text-xs px-2 py-0.5 rounded hover:bg-muted text-muted-foreground"
                    >
                      Collapse all
                    </button>
                  </div>
                </div>

                {parsedDiffFiles.map((file, fileIdx) => {
                  const isExpanded = expandedDiffFiles.has(fileIdx);
                  const totalChanges = file.additions + file.deletions;
                  const addPct = totalChanges > 0 ? Math.round((file.additions / totalChanges) * 100) : 0;
                  return (
                    <div key={fileIdx} className="rounded-lg border overflow-hidden">
                      {/* File header */}
                      <button
                        onClick={() => toggleDiffFile(fileIdx)}
                        className="w-full flex items-center gap-2 px-3 py-2 text-left transition-colors bg-muted/40 hover:bg-muted/70"
                      >
                        {isExpanded
                          ? <FontAwesomeIcon icon={faChevronDown} className="h-3.5 w-3.5 flex-shrink-0 text-muted-foreground" />
                          : <FontAwesomeIcon icon={faChevronRight} className="h-3.5 w-3.5 flex-shrink-0 text-muted-foreground" />
                        }
                        <FontAwesomeIcon icon={faFile} className="h-3.5 w-3.5 flex-shrink-0 text-blue-500" />
                        <span className="text-xs font-mono truncate flex-1">
                          {file.filename}
                        </span>
                        <div className="flex items-center gap-1.5 flex-shrink-0 ml-2">
                          <span className="text-xs font-mono font-medium text-emerald-600 dark:text-emerald-400">
                            +{file.additions}
                          </span>
                          <span className="text-xs font-mono font-medium text-rose-600 dark:text-rose-400">
                            -{file.deletions}
                          </span>
                          <div className="flex h-2 w-12 rounded-full overflow-hidden bg-muted ml-1">
                            <div className="bg-emerald-500" style={{ width: `${addPct}%` }} />
                            <div className="bg-rose-500" style={{ width: `${100 - addPct}%` }} />
                          </div>
                        </div>
                      </button>

                      {/* File diff lines */}
                      {isExpanded && (
                        <div className="overflow-x-auto">
                          {file.lines.map((line, lineIdx) => {
                            let className = 'font-mono text-xs whitespace-pre px-4 py-0.5';
                            if (line.startsWith('+') && !line.startsWith('+++')) {
                              className += ' bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-200';
                            } else if (line.startsWith('-') && !line.startsWith('---')) {
                              className += ' bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-200';
                            } else if (line.startsWith('@@')) {
                              className += ' bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-200';
                            } else if (line.startsWith('diff --git') || line.startsWith('index ') || line.startsWith('---') || line.startsWith('+++')) {
                              className += ' font-semibold text-muted-foreground bg-muted/30';
                            }
                            return <div key={lineIdx} className={className}>{line || ' '}</div>;
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            ) : (
              <pre className="font-mono text-xs whitespace-pre-wrap text-muted-foreground p-4">{diff}</pre>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Live Board / TV Mode */}
      {showLiveBoard && (
        <LiveBoard
          stats={stats}
          commits={commits}
          repoName={selectedPredefined?.name || localPath || remoteUrl || 'Repository'}
          onClose={() => setShowLiveBoard(false)}
          isLive={autoRefresh}
          isRefreshing={isAutoRefreshing}
          lastRefresh={lastAutoRefresh || lastRefresh}
          repoConfig={buildConfig()}
        />
      )}

      {/* Individual Author Dialog */}
      <Dialog open={showAuthorDialog} onOpenChange={setShowAuthorDialog}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3">
              <FontAwesomeIcon icon={faUsers} className="h-5 w-5" />
              {selectedAuthor?.name}
            </DialogTitle>
          </DialogHeader>
          
          {selectedAuthor && (
            <div className="space-y-6">
              {/* Stats Summary */}
              <div className="grid grid-cols-3 gap-4">
                <div className="text-center p-4 rounded-xl border border-black/[0.06] dark:border-white/[0.06] bg-white/40 dark:bg-white/[0.04] backdrop-blur-sm">
                  <div className="text-2xl font-bold">{selectedAuthor.commits}</div>
                  <div className="text-xs text-muted-foreground">Commits</div>
                </div>
                <div className="text-center p-4 rounded-xl border border-black/[0.06] dark:border-white/[0.06] bg-white/40 dark:bg-white/[0.04] backdrop-blur-sm">
                  <div className="text-2xl font-bold">{selectedAuthor.activeDays}</div>
                  <div className="text-xs text-muted-foreground">Active Days</div>
                </div>
                <div className="text-center p-4 rounded-xl border border-black/[0.06] dark:border-white/[0.06] bg-white/40 dark:bg-white/[0.04] backdrop-blur-sm">
                  <div className="text-2xl font-bold text-green-600 dark:text-emerald-400">+{formatNumber(selectedAuthor.additions)}</div>
                  <div className="text-xs text-muted-foreground">Lines Added</div>
                </div>
              </div>

              {/* Performance Targets */}
              <div className="border border-black/[0.06] dark:border-white/[0.06] rounded-xl p-4">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-semibold flex items-center gap-2">
                    <FontAwesomeIcon icon={faArrowTrendUp} className="h-4 w-4" />
                    Performance Targets
                  </h3>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => setShowTargetForm(!showTargetForm)}
                  >
                    {showTargetForm ? 'Cancel' : '+ Add Target'}
                  </Button>
                </div>

                {showTargetForm && (
                  <div className="mb-4 p-4 rounded-xl border border-black/[0.06] dark:border-white/[0.06] bg-white/40 dark:bg-white/[0.04] space-y-3">
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="text-xs text-muted-foreground">Period Start <span className="text-red-500">*</span></label>
                        <input
                          type="date"
                          className="w-full px-3 py-2 border border-black/[0.08] dark:border-white/[0.08] bg-white/60 dark:bg-white/[0.04] rounded-lg text-sm"
                          value={newTarget.periodStart}
                          onChange={(e) => setNewTarget({ ...newTarget, periodStart: e.target.value })}
                        />
                      </div>
                      <div>
                        <label className="text-xs text-muted-foreground">Period End <span className="text-red-500">*</span></label>
                        <input
                          type="date"
                          className="w-full px-3 py-2 border border-black/[0.08] dark:border-white/[0.08] bg-white/60 dark:bg-white/[0.04] rounded-lg text-sm"
                          value={newTarget.periodEnd}
                          onChange={(e) => setNewTarget({ ...newTarget, periodEnd: e.target.value })}
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-3 gap-3">
                      <div>
                        <label className="text-xs text-muted-foreground">Min Commits</label>
                        <input
                          type="number"
                          className="w-full px-3 py-2 border border-black/[0.08] dark:border-white/[0.08] bg-white/60 dark:bg-white/[0.04] rounded-lg text-sm"
                          placeholder="e.g. 20"
                          value={newTarget.minCommits}
                          onChange={(e) => setNewTarget({ ...newTarget, minCommits: e.target.value })}
                        />
                      </div>
                      <div>
                        <label className="text-xs text-muted-foreground">Min Active Days</label>
                        <input
                          type="number"
                          className="w-full px-3 py-2 border border-black/[0.08] dark:border-white/[0.08] bg-white/60 dark:bg-white/[0.04] rounded-lg text-sm"
                          placeholder="e.g. 15"
                          value={newTarget.minActiveDays}
                          onChange={(e) => setNewTarget({ ...newTarget, minActiveDays: e.target.value })}
                        />
                      </div>
                      <div>
                        <label className="text-xs text-muted-foreground">Min Additions</label>
                        <input
                          type="number"
                          className="w-full px-3 py-2 border border-black/[0.08] dark:border-white/[0.08] bg-white/60 dark:bg-white/[0.04] rounded-lg text-sm"
                          placeholder="e.g. 500"
                          value={newTarget.minAdditions}
                          onChange={(e) => setNewTarget({ ...newTarget, minAdditions: e.target.value })}
                        />
                      </div>
                    </div>
                    <div>
                      <label className="text-xs text-muted-foreground">Reason for Target <span className="text-red-500">*</span></label>
                      <textarea
                        className="w-full px-3 py-2 border border-black/[0.08] dark:border-white/[0.08] bg-white/60 dark:bg-white/[0.04] rounded-lg text-sm"
                        rows={2}
                        placeholder="Why is this target being set? (required)"
                        value={newTarget.reason}
                        onChange={(e) => setNewTarget({ ...newTarget, reason: e.target.value })}
                      />
                    </div>
                    <div>
                      <label className="text-xs text-muted-foreground">Additional Notes</label>
                      <textarea
                        className="w-full px-3 py-2 border border-black/[0.08] dark:border-white/[0.08] bg-white/60 dark:bg-white/[0.04] rounded-lg text-sm"
                        rows={2}
                        placeholder="Optional notes..."
                        value={newTarget.notes}
                        onChange={(e) => setNewTarget({ ...newTarget, notes: e.target.value })}
                      />
                    </div>
                    <Button 
                      size="sm"
                      disabled={!newTarget.reason.trim() || !newTarget.periodStart || !newTarget.periodEnd}
                      onClick={async () => {
                        if (!newTarget.reason.trim()) return;
                        
                        const target = {
                          authorName: selectedAuthor.name,
                          authorEmail: selectedAuthor.email,
                          period: {
                            start: newTarget.periodStart ? new Date(newTarget.periodStart).toISOString() : (dateRange?.from?.toISOString() || new Date().toISOString()),
                            end: newTarget.periodEnd ? new Date(newTarget.periodEnd).toISOString() : (dateRange?.to?.toISOString() || new Date().toISOString()),
                          },
                          targets: {
                            minCommits: newTarget.minCommits ? parseInt(newTarget.minCommits) : undefined,
                            minActiveDays: newTarget.minActiveDays ? parseInt(newTarget.minActiveDays) : undefined,
                            minAdditions: newTarget.minAdditions ? parseInt(newTarget.minAdditions) : undefined,
                          },
                          reason: newTarget.reason,
                          notes: newTarget.notes || undefined,
                          status: 'active' as const,
                        };
                        
                        const created = await api.createTarget(target);
                        
                        if (created) {
                          setAuthorTargets([...authorTargets, created]);
                          setAllTargets([...allTargets, created]);
                          setShowTargetForm(false);
                          setNewTarget({ minCommits: '', minActiveDays: '', minAdditions: '', periodStart: '', periodEnd: '', notes: '', reason: '' });
                        }
                      }}
                    >
                      Create Target
                    </Button>
                  </div>
                )}

                {authorTargets.length === 0 && !showTargetForm ? (
                  <p className="text-sm text-muted-foreground text-center py-4">
                    No performance targets set for this contributor
                  </p>
                ) : (
                  <div className="space-y-3">
                    {authorTargets.map((target) => (
                      <div key={target.id} className={`p-3 border border-black/[0.06] dark:border-white/[0.06] rounded-xl text-sm ${
                        target.status === 'cancelled' ? 'opacity-60' : ''
                      }`}>
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <span className={`px-2 py-0.5 rounded text-xs ${
                              target.status === 'active' ? 'bg-green-100 text-green-700 dark:bg-green-500/20 dark:text-green-300' :
                              target.status === 'completed' ? 'bg-blue-100 text-blue-700 dark:bg-blue-500/20 dark:text-blue-300' :
                              'bg-gray-100 text-gray-700 dark:bg-white/[0.06] dark:text-slate-400'
                            }`}>
                              {target.status.toUpperCase()}
                            </span>
                            <span className="text-xs text-muted-foreground">
                              Created {new Date(target.createdAt).toLocaleDateString()}
                            </span>
                          </div>
                          {target.status === 'active' && (
                            <button
                              className="text-xs text-red-500 hover:text-red-700 hover:underline"
                              onClick={() => setDeleteTargetId(target.id)}
                            >
                              Remove
                            </button>
                          )}
                        </div>
                        
                        {/* Reason for target */}
                        {target.reason && (
                          <div className="mb-2 p-2 bg-blue-50 dark:bg-blue-900/20 rounded text-xs">
                            <span className="font-medium text-blue-700 dark:text-blue-300">Reason: </span>
                            <span className="text-blue-600 dark:text-blue-400">{target.reason}</span>
                          </div>
                        )}
                        
                        {/* Cancel reason if cancelled */}
                        {target.status === 'cancelled' && target.cancelReason && (
                          <div className="mb-2 p-2 bg-red-50 dark:bg-red-900/20 rounded text-xs">
                            <span className="font-medium text-red-700 dark:text-red-300">Removed: </span>
                            <span className="text-red-600 dark:text-red-400">{target.cancelReason}</span>
                          </div>
                        )}
                        
                        <div className="grid grid-cols-3 gap-2 text-xs">
                          {target.targets.minCommits && (
                            <div>
                              <span className="text-muted-foreground">Commits:</span>{' '}
                              <span className={selectedAuthor.commits >= target.targets.minCommits ? 'text-green-600' : 'text-red-600'}>
                                {selectedAuthor.commits}/{target.targets.minCommits}
                              </span>
                            </div>
                          )}
                          {target.targets.minActiveDays && (
                            <div>
                              <span className="text-muted-foreground">Days:</span>{' '}
                              <span className={selectedAuthor.activeDays >= target.targets.minActiveDays ? 'text-green-600' : 'text-red-600'}>
                                {selectedAuthor.activeDays}/{target.targets.minActiveDays}
                              </span>
                            </div>
                          )}
                          {target.targets.minAdditions && (
                            <div>
                              <span className="text-muted-foreground">Additions:</span>{' '}
                              <span className={selectedAuthor.additions >= target.targets.minAdditions ? 'text-green-600' : 'text-red-600'}>
                                {selectedAuthor.additions}/{target.targets.minAdditions}
                              </span>
                            </div>
                          )}
                        </div>
                        {target.notes && (
                          <div className="mt-2 text-xs text-muted-foreground italic">
                            {target.notes}
                          </div>
                        )}
                        
                        {/* Delete confirmation inline */}
                        {deleteTargetId === target.id && (
                          <div className="mt-3 pt-3 border-t border-black/[0.06] dark:border-white/[0.06] space-y-2">
                            <div className="text-xs font-medium text-red-600 dark:text-red-400">Remove this target?</div>
                            <textarea
                              className="w-full px-2 py-1.5 border border-red-200 dark:border-red-500/30 bg-white/60 dark:bg-white/[0.04] rounded-lg text-xs"
                              rows={2}
                              placeholder="Reason for removal (required)"
                              value={deleteReason}
                              onChange={(e) => setDeleteReason(e.target.value)}
                            />
                            <div className="flex gap-2">
                              <Button
                                size="sm"
                                variant="destructive"
                                className="text-xs h-7"
                                disabled={!deleteReason.trim()}
                                onClick={async () => {
                                  if (!deleteReason.trim()) return;
                                  
                                  // Update target status to cancelled with reason
                                  const updated = await api.updateTarget(target.id, {
                                    status: 'cancelled',
                                    cancelReason: deleteReason,
                                  });
                                  
                                  if (updated) {
                                    setAuthorTargets(authorTargets.map(t => 
                                      t.id === target.id ? updated : t
                                    ));
                                    setAllTargets(allTargets.map(t => 
                                      t.id === target.id ? updated : t
                                    ));
                                    setDeleteTargetId(null);
                                    setDeleteReason('');
                                  }
                                }}
                              >
                                Confirm Remove
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                className="text-xs h-7"
                                onClick={() => {
                                  setDeleteTargetId(null);
                                  setDeleteReason('');
                                }}
                              >
                                Cancel
                              </Button>
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Actions */}
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => {
                    setAuthorFilter(selectedAuthor.name);
                    setShowAuthorDialog(false);
                    setActiveTab('commits');
                  }}
                >
                  <FontAwesomeIcon icon={faCodeCommit} className="h-4 w-4 mr-2" />
                  View Commits
                </Button>
                <Button
                  className="flex-1"
                  onClick={async () => {
                    const indAliases = getStoredGitAliases();
                    const authorCommits = commits.filter(c => resolveGitAuthor(c.author, indAliases) === selectedAuthor.name)
                      .map(c => ({ ...c, author: resolveGitAuthor(c.author, indAliases) }));
                    
                    // Get target status for this author
                    const targetStatus = getAuthorTargetStatus(selectedAuthor);
                    let performanceReview = null;
                    
                    if (targetStatus.hasTarget && targetStatus.target) {
                      performanceReview = {
                        target: targetStatus.target,
                        actual: {
                          commits: selectedAuthor.commits,
                          activeDays: selectedAuthor.activeDays,
                          additions: selectedAuthor.additions,
                          deletions: selectedAuthor.deletions,
                          netLines: selectedAuthor.additions - selectedAuthor.deletions,
                        },
                        metrics: {
                          commitsProgress: targetStatus.target.targets.minCommits 
                            ? Math.min(100, (selectedAuthor.commits / targetStatus.target.targets.minCommits) * 100) : 100,
                          activeDaysProgress: targetStatus.target.targets.minActiveDays 
                            ? Math.min(100, (selectedAuthor.activeDays / targetStatus.target.targets.minActiveDays) * 100) : 100,
                          additionsProgress: targetStatus.target.targets.minAdditions 
                            ? Math.min(100, (selectedAuthor.additions / targetStatus.target.targets.minAdditions) * 100) : 100,
                          deletionsProgress: 100,
                          netLinesProgress: 100,
                          overallProgress: targetStatus.progress,
                        },
                        status: targetStatus.status,
                      };
                    }
                    
                    generateAndDownloadReport('individual', {
                        repoName: selectedPredefined?.name || localPath || remoteUrl || 'Repository',
                        repoType,
                        generatedAt: new Date().toISOString(),
                        author: selectedAuthor,
                        commits: authorCommits,
                        dateRange: {
                          start: dateRange?.from?.toISOString() || '',
                          end: dateRange?.to?.toISOString() || '',
                        },
                        performanceReview,
                      }, `verigit-${selectedAuthor.name.replace(/[^a-z0-9]/gi, '-')}-report.pdf`);
                  }}
                >
                  <FontAwesomeIcon icon={faDownload} className="h-4 w-4 mr-2" />
                  Download Individual Report
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default App;
